import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentSessionHistoryView } from "./discovery/AgentSessionHistoryView";
import {
  Upload,
  Database,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Info,
  Search,
  BookMarked,
  ShieldCheck,
  Eye,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Table2,
  ChevronDown,
  ChevronRight,
  X,
  Send,
  Loader2,
  Check,
  Edit3,
  ChevronUp,
  FileText,
  Lock,
  Shield,
  BarChart3,
  Copy,
  Code2,
  User,
  Tag,
  ClipboardCheck,
  MessageCircle,
} from "lucide-react";
import { api } from "../api";
import { apiv2, type DBConfigOut } from "../apiv2";
import type {
  MetadataColumnEntry,
  MetadataClarification,
  MetadataUploadResponse,
  MetadataDbConnectParams,
  MetadataSheetInfo,
  SavedMetadataSession,
  GlossaryEntry,
  ChatEntry,
  DatasetProfile,
  DDLResult,
  SupportedDatabase,
} from "../types";
import GreenTendrils from "./GreenTendrils";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";

interface Props {
  onBack: () => void;
  projectId?: string;
  resumeSessionId?: string;
}

// Keeps the legacy manual-connect modal's port field in sync with the
// selected engine (same map as ConnectDatabaseDialog's DEFAULT_PORTS).
const DEFAULT_DB_PORTS: Record<string, number> = {
  postgresql: 5432,
  mysql: 3306,
  mssql: 1433,
  oracle: 1521,
};

/* ── Step indicator ── */
const STEPS = [
  { label: "Upload / Connect", icon: Upload },
  { label: "Auto-Discover", icon: BarChart3 },
  { label: "AI Enrichment", icon: Sparkles },
  { label: "Clarify & Review", icon: CheckCircle2 },
  { label: "Publish & Export", icon: Download },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = i + 1 === current;
        const isDone = i + 1 < current;
        return (
          <div key={i} className="flex items-center gap-1">
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-300 ${
                isActive
                  ? "bg-brand-500/20 text-brand-400 border border-brand-500/40"
                  : isDone
                    ? "bg-brand-500/10 text-brand-500/60 border border-brand-500/20"
                    : "bg-white/3 text-white/20 border border-white/5"
              }`}
            >
              {isDone ? (
                <Check size={12} className="text-brand-500" />
              ) : (
                <Icon size={12} />
              )}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`w-6 h-px ${isDone ? "bg-brand-500/40" : "bg-white/8"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Sensitivity badge ── */
function SensitivityBadge({ level }: { level: string }) {
  const map: Record<string, { bg: string; text: string }> = {
    PII: { bg: "bg-red-500/15 border-red-500/30", text: "text-red-400" },
    Confidential: {
      bg: "bg-orange-500/15 border-orange-500/30",
      text: "text-orange-400",
    },
    Internal: {
      bg: "bg-yellow-500/15 border-yellow-500/30",
      text: "text-yellow-400",
    },
    Public: {
      bg: "bg-green-500/15 border-green-500/30",
      text: "text-green-400",
    },
  };
  const s = map[level] || map.Public;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold border ${s.bg} ${s.text}`}
    >
      {level === "PII" && <Shield size={10} />}
      {level === "Confidential" && <Lock size={10} />}
      {level}
    </span>
  );
}

/* ── Confidence bar ── */
function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color =
    pct >= 90 ? "bg-green-500" : pct >= 70 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[11px] text-white/40">{pct}%</span>
    </div>
  );
}

/* ── Key-constraint badges (PK / FK / Unique) ── */
function KeyBadges({ col }: { col: MetadataColumnEntry }) {
  if (!col.is_primary_key && !col.is_foreign_key && !col.is_unique) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-1.5 align-middle">
      {col.is_primary_key && (
        <span
          title={`Primary key (${col.key_type || "inferred"})`}
          className="px-1 py-0.5 rounded text-[9px] font-bold border bg-indigo-500/15 border-indigo-500/30 text-indigo-400"
        >
          PK
        </span>
      )}
      {col.is_foreign_key && (
        <span
          title={`Foreign key → ${col.foreign_key_ref || "?"}${
            col.fk_confidence != null
              ? ` (confidence ${Math.round(col.fk_confidence * 100)}%)`
              : ""
          }`}
          className="inline-flex items-center gap-1"
        >
          <span className="px-1 py-0.5 rounded text-[9px] font-bold border bg-cyan-500/15 border-cyan-500/30 text-cyan-400">
            FK
          </span>
          {col.foreign_key_ref && (
            <span className="text-[9px] text-cyan-400/70 font-mono max-w-[110px] truncate">
              → {col.foreign_key_ref}
            </span>
          )}
        </span>
      )}
      {col.is_unique && (
        <span
          title={`Unique (${col.unique_key_type || "inferred"})`}
          className="px-1 py-0.5 rounded text-[9px] font-bold border bg-teal-500/15 border-teal-500/30 text-teal-400"
        >
          UQ
        </span>
      )}
    </span>
  );
}

/* ── Status badge ── */
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; text: string }> = {
    draft: { bg: "bg-white/5 border-white/10", text: "text-white/30" },
    enriched: {
      bg: "bg-blue-500/15 border-blue-500/30",
      text: "text-blue-400",
    },
    clarified: {
      bg: "bg-purple-500/15 border-purple-500/30",
      text: "text-purple-400",
    },
    approved: {
      bg: "bg-green-500/15 border-green-500/30",
      text: "text-green-400",
    },
    published: {
      bg: "bg-brand-500/15 border-brand-500/30",
      text: "text-brand-400",
    },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${s.bg} ${s.text}`}
    >
      {status.toUpperCase()}
    </span>
  );
}

/* ── Per-table metadata download buttons (full catalog + PII-only) ── */
function TableExportButtons({
  sessionId,
  tableName,
}: {
  sessionId: string | null;
  tableName: string;
}) {
  if (!sessionId) return null;
  const open = (
    format: string,
    opts?: { tableName?: string; piiOnly?: boolean },
  ) => {
    window.open(api.metadataExportUrl(sessionId, format, opts), "_blank");
  };
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          open("excel", { tableName });
        }}
        title="Download this table's metadata (Excel)"
        aria-label="Download this table's metadata (Excel)"
        className="p-1 rounded hover:bg-white/5 text-white/25 hover:text-brand-400 transition-all"
      >
        <FileSpreadsheet size={12} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          open("pdf", { tableName });
        }}
        title="Download this table's metadata (PDF)"
        aria-label="Download this table's metadata (PDF)"
        className="p-1 rounded hover:bg-white/5 text-white/25 hover:text-brand-400 transition-all"
      >
        <FileText size={12} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          open("excel", { tableName, piiOnly: true });
        }}
        title="Download PII + Confidential fields for this table (Excel)"
        aria-label="Download PII and Confidential fields for this table (Excel)"
        className="p-1 rounded hover:bg-white/5 text-white/25 hover:text-red-400 transition-all"
      >
        <Shield size={12} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
export default function MetadataCatalogView({
  onBack,
  projectId,
  resumeSessionId,
}: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);

  // Source selection
  const [sourceType, setSourceType] = useState<"file" | "database">("file");

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sourceName, setSourceName] = useState("");
  const [profile, setProfile] = useState<DatasetProfile | null>(null);
  const [columnsMetadata, setColumnsMetadata] = useState<MetadataColumnEntry[]>(
    [],
  );
  const [clarifications, setClarifications] = useState<MetadataClarification[]>(
    [],
  );
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [glossaryMarkdown, setGlossaryMarkdown] = useState("");
  const [lineageMermaid, setLineageMermaid] = useState("");

  // Multi-sheet
  const [pendingSheets, setPendingSheets] = useState<MetadataSheetInfo[]>([]);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [showSheetSelect, setShowSheetSelect] = useState(false);

  // Sheet grouping for multi-sheet views
  const [expandedSheets, setExpandedSheets] = useState<Set<string>>(new Set());
  const [filterSheet, setFilterSheet] = useState<string>("all");
  const [statsFocus, setStatsFocus] = useState<"pii" | "confidential" | null>(
    null,
  );
  const sheetRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // DB connect — project-level connections
  const [dbTables, setDbTables] = useState<string[]>([]);
  const [dbViews, setDbViews] = useState<string[]>([]);
  const [showDbConnect, setShowDbConnect] = useState(false);
  const [dbConfig, setDbConfig] = useState<MetadataDbConnectParams>({
    db_type: "postgresql",
    host: "",
    port: DEFAULT_DB_PORTS.postgresql,
    database: "",
    username: "",
    password: "",
  });
  const [selectedDbTable, setSelectedDbTable] = useState("");
  const [projectDbConnections, setProjectDbConnections] = useState<
    { label: string; type: "source" | "target"; config: DBConfigOut }[]
  >([]);
  const [selectedConnectionType, setSelectedConnectionType] = useState<
    "source" | "target"
  >("source");
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  // Manual connect dialog — mirrors Data Profiler's "Connect Database" modal
  // so users can connect ad hoc even without a pre-configured project DB.
  const [showManualDbModal, setShowManualDbModal] = useState(false);
  const [dbObjectFilter, setDbObjectFilter] = useState<
    "tables" | "views" | "all"
  >("all");
  const [dbSearchTerm, setDbSearchTerm] = useState("");
  const [selectedDbObjects, setSelectedDbObjects] = useState<Set<string>>(
    new Set(),
  );

  // Glossary mode
  const [glossaryMode, setGlossaryMode] = useState<"generate" | "update">(
    "generate",
  );

  // Loading / error
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Search / filter for review table
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTable, setSearchTable] = useState("");
  const [filterSensitivity, setFilterSensitivity] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Editing
  const [editingCol, setEditingCol] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});

  // Expanded detail row
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Chat
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatDeterministicOnly, setChatDeterministicOnly] = useState(false);
  // Floating widget so chat stays reachable without scrolling to Step 5.
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Clarification answers
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<string, string>>(
    {},
  );
  const [clarifyLoading, setClarifyLoading] = useState<string | null>(null);
  // Collapsed by default — with dozens of clarifications this section can
  // otherwise push the review table and Publish button far below the fold.
  const [clarificationsExpanded, setClarificationsExpanded] = useState(false);

  // Export
  const [exportFormat, setExportFormat] = useState("json");

  // Saved sessions
  const [savedSessions, setSavedSessions] = useState<SavedMetadataSession[]>(
    [],
  );
  const [showSavedSessions, setShowSavedSessions] = useState(false);
  const [resumeLoading, setResumeLoading] = useState<string | null>(null);

  // Structured glossary
  const [glossaryEntries, setGlossaryEntries] = useState<GlossaryEntry[]>([]);
  const [glossarySearch, setGlossarySearch] = useState("");
  const [glossaryDomainFilter, setGlossaryDomainFilter] = useState("all");

  // Dataset summary
  const [datasetSummary, setDatasetSummary] = useState("");
  const [tableDescriptions, setTableDescriptions] = useState<
    Record<string, string>
  >({});

  // DDL Generator — extracts DDL straight from the profiled source metadata
  // (native source types), translated for the chosen target database.
  const [ddlDb, setDdlDb] = useState("snowflake");
  const [ddlType, setDdlType] = useState("create_table");
  const [ddlResult, setDdlResult] = useState<DDLResult | null>(null);
  const [ddlLoading, setDdlLoading] = useState(false);
  const [ddlTableMode, setDdlTableMode] = useState<"all" | "selected">("all");
  const [ddlSelectedTables, setDdlSelectedTables] = useState<string[]>([]);
  const [ddlSchemaName, setDdlSchemaName] = useState("");
  const [ddlCopied, setDdlCopied] = useState(false);
  const [supportedDbs, setSupportedDbs] = useState<SupportedDatabase[]>([]);

  // Drag and drop
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // ── File Upload ──
  const handleFileUpload = useCallback(
    async (file: File) => {
      setLoading(true);
      setLoadingMsg("Uploading and analyzing...");
      setError(null);
      try {
        const res = await api.metadataUpload(file, glossaryMode, projectId);
        setSessionId(res.session_id);
        setSourceName(res.source_name || file.name);

        if (res.is_multi_sheet && res.sheets) {
          setPendingSheets(res.sheets);
          setShowSheetSelect(true);
          setLoading(false);
          return;
        }

        setProfile(res.profile);
        setColumnsMetadata(res.columns_metadata);
        setStep(2);
      } catch (e: any) {
        setError(e.message || "Upload failed");
      } finally {
        setLoading(false);
        setLoadingMsg("");
      }
    },
    [glossaryMode, projectId],
  );

  // ── Sheet selection ──
  const handleSheetSelect = useCallback(async () => {
    if (!sessionId || selectedSheets.length === 0) return;
    setLoading(true);
    setLoadingMsg("Analyzing selected sheets...");
    try {
      const res = await api.metadataSelectSheets(
        sessionId,
        selectedSheets,
        projectId,
      );
      setProfile(res.profile);
      setColumnsMetadata(res.columns_metadata);
      setShowSheetSelect(false);
      setStep(2);
    } catch (e: any) {
      setError(e.message || "Sheet selection failed");
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [sessionId, selectedSheets, projectId]);

  // ── DB Connect (saved project connection) ──
  const handleDbConnect = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadingMsg("Connecting to database...");
    setError(null);
    try {
      const res = await api.metadataProjectDbTables(
        projectId,
        selectedConnectionType,
      );
      setSessionId(res.session_id);
      setDbTables(res.tables || []);
      setDbViews(res.views || []);
    } catch (e: any) {
      setError(e.message || "Connection failed");
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [projectId, selectedConnectionType]);

  // ── DB Connect (manual — Connect Database modal) ──
  const handleManualDbConnect = useCallback(async () => {
    setShowManualDbModal(false);
    setLoading(true);
    setLoadingMsg("Connecting to database...");
    setError(null);
    try {
      const res = await api.metadataDbConnect({
        ...dbConfig,
        project_id: projectId,
      });
      setSessionId(res.session_id);

      if ("tables" in res && Array.isArray(res.tables)) {
        setDbTables(res.tables);
        setLoading(false);
        return;
      }

      const uploadRes = res as MetadataUploadResponse;
      setSourceName(uploadRes.source_name || "");
      setProfile(uploadRes.profile);
      setColumnsMetadata(uploadRes.columns_metadata);
      setShowDbConnect(false);
      setStep(2);
    } catch (e: any) {
      setError(e.message || "Connection failed");
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [dbConfig, projectId]);

  // Additive entry point: reuse ANY connection saved by another agent for
  // this project (not just the single source/target slot the buttons above
  // read via projectDbConnections) — see
  // admin/project_db_config.list_connections_for_project.
  const [showSharedConnectDialog, setShowSharedConnectDialog] = useState(false);

  const applySharedDbConnectResult = useCallback(
    (
      res:
        | MetadataUploadResponse
        | { session_id: string; tables?: string[]; views?: string[] },
    ) => {
      setSessionId(res.session_id);
      if ("tables" in res && Array.isArray(res.tables)) {
        setDbTables(res.tables);
        setDbViews((res as { views?: string[] }).views || []);
        return;
      }
      const uploadRes = res as MetadataUploadResponse;
      setSourceName(uploadRes.source_name || "");
      setProfile(uploadRes.profile);
      setColumnsMetadata(uploadRes.columns_metadata);
      setShowDbConnect(false);
      setStep(2);
    },
    [],
  );

  const handleSharedDialogConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const res = await api.metadataDbConnect({
          db_type: values.db_type,
          host: values.host,
          port: parseInt(values.port, 10),
          database: values.database,
          schema_name: values.schema_name,
          username: values.username,
          password: values.password,
          account: values.account,
          warehouse: values.warehouse,
          role: values.role,
          table: dbConfig.table,
          project_id: projectId,
        });
        applySharedDbConnectResult(res);
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err.message || "Connection failed" };
      }
    },
    [dbConfig.table, projectId, applySharedDbConnectResult],
  );

  const handleSharedDialogConnectExisting = useCallback(
    async (connectionId: string): Promise<ConnectResult> => {
      try {
        const res = await api.metadataDbConnect({
          connection_id: connectionId,
          table: dbConfig.table,
          project_id: projectId,
        });
        applySharedDbConnectResult(res);
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err.message || "Connection failed" };
      }
    },
    [dbConfig.table, projectId, applySharedDbConnectResult],
  );

  // ── Toggle table/view selection ──
  const handleToggleDbObject = useCallback((name: string) => {
    setSelectedDbObjects((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // ── Select / deselect all visible objects ──
  const handleToggleAllVisible = useCallback((visibleItems: string[]) => {
    setSelectedDbObjects((prev) => {
      const allSelected = visibleItems.every((item) => prev.has(item));
      const next = new Set(prev);
      if (allSelected) {
        visibleItems.forEach((item) => next.delete(item));
      } else {
        visibleItems.forEach((item) => next.add(item));
      }
      return next;
    });
  }, []);

  // ── Discover selected tables/views ──
  const handleProfileSelectedTables = useCallback(async () => {
    if (!sessionId || selectedDbObjects.size === 0) return;
    const tables = Array.from(selectedDbObjects);
    setLoading(true);
    setLoadingMsg(
      `Discovering ${tables.length} table${tables.length > 1 ? "s" : ""}...`,
    );
    try {
      const res = await api.metadataDbSelectTables(
        sessionId,
        tables,
        projectId,
      );
      setSourceName(
        res.source_name ||
          `${tables.length} table${tables.length > 1 ? "s" : ""} selected`,
      );
      setProfile(res.profile);
      setColumnsMetadata(res.columns_metadata);
      setDbTables([]);
      setDbViews([]);
      setSelectedDbObjects(new Set());
      setDbSearchTerm("");
      setShowDbConnect(false);
      setStep(2);
    } catch (e: any) {
      setError(e.message || "Failed to discover selected tables");
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [sessionId, projectId, selectedDbObjects]);

  // ── Select table from DB (legacy single-select) ──
  const handleSelectTable = useCallback(
    async (table: string) => {
      if (!sessionId) return;
      setLoading(true);
      setLoadingMsg(`Discovering table ${table}...`);
      try {
        const res = await api.metadataDbSelectTable(
          sessionId,
          table,
          projectId,
        );
        setSourceName(res.source_name || table);
        setProfile(res.profile);
        setColumnsMetadata(res.columns_metadata);
        setDbTables([]);
        setShowDbConnect(false);
        setStep(2);
      } catch (e: any) {
        setError(e.message || "Failed to discover table");
      } finally {
        setLoading(false);
        setLoadingMsg("");
      }
    },
    [sessionId, projectId],
  );

  // ── Trigger LLM enrichment ──
  const handleEnrich = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setLoadingMsg("AI is analyzing your metadata...");
    setError(null);
    try {
      const res = await api.metadataEnrich(sessionId, glossaryMode);
      setColumnsMetadata(res.columns_metadata);
      setClarifications(res.clarifications);
      setGlossaryMarkdown(res.glossary_markdown);
      setLineageMermaid(res.lineage_mermaid);
      if (res.dataset_summary) setDatasetSummary(res.dataset_summary);
      if ((res as any).table_descriptions)
        setTableDescriptions((res as any).table_descriptions);
      setStep(4);
    } catch (e: any) {
      setError(e.message || "Enrichment failed");
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [sessionId, glossaryMode]);

  // ── Clarify ──
  const handleClarify = useCallback(
    async (
      columnName: string,
      groupColumns?: string[],
      answerOverride?: string,
    ) => {
      if (!sessionId) return;
      const answer = answerOverride ?? clarifyAnswers[columnName];
      if (!answer?.trim()) return;

      setClarifyLoading(columnName);
      try {
        const res = await api.metadataClarify({
          session_id: sessionId,
          column_name: columnName,
          answer: answer.trim(),
          group_columns: groupColumns,
        });
        // Update all columns in the group in local state
        const updatedCols =
          res.updated_columns || (res.column ? [res.column] : []);
        const updatedNames = new Set(
          updatedCols.map((c: any) => c.column_name),
        );

        setColumnsMetadata((prev) =>
          prev.map((c) =>
            updatedNames.has(c.column_name)
              ? {
                  ...c,
                  ...updatedCols.find(
                    (u: any) => u.column_name === c.column_name,
                  ),
                }
              : c,
          ),
        );
        // Remove all columns in this group from clarifications
        const allGroupCols = new Set(groupColumns || [columnName]);
        setClarifications((prev) =>
          prev.filter((c) => {
            const clarCols = c.columns || [c.column_name];
            return !clarCols.some((cn) => allGroupCols.has(cn));
          }),
        );
        setClarifyAnswers((prev) => ({ ...prev, [columnName]: "" }));
      } catch (e: any) {
        setError(e.message || "Clarification failed");
      } finally {
        setClarifyLoading(null);
      }
    },
    [sessionId, clarifyAnswers],
  );

  // ── Chat ──
  const handleChat = useCallback(async () => {
    if (!sessionId || !chatInput.trim()) return;
    const message = chatInput.trim();
    setChatInput("");
    setChatHistory((prev) => [...prev, { role: "user", content: message }]);
    setChatLoading(true);
    try {
      const res = await api.metadataChat(
        sessionId,
        message,
        chatDeterministicOnly,
      );
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: res.response },
      ]);
      if (res.columns_metadata) {
        setColumnsMetadata(res.columns_metadata);
      }
    } catch (e: any) {
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${e.message}` },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [sessionId, chatInput, chatDeterministicOnly]);

  // ── Approve single ──
  const handleApprove = useCallback(
    async (columnName: string) => {
      if (!sessionId) return;
      try {
        const res = await api.metadataApproveColumn(sessionId, columnName);
        setColumnsMetadata(res.columns_metadata);
      } catch (e: any) {
        setError(e.message);
      }
    },
    [sessionId],
  );

  // ── Approve all (optionally scoped to a single table) ──
  const handleApproveAll = useCallback(
    async (tableName?: string) => {
      if (!sessionId) return;
      setLoading(true);
      try {
        const res = await api.metadataApproveAll(sessionId, tableName);
        setColumnsMetadata(res.columns_metadata);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  // ── Save column edit ──
  const handleSaveEdit = useCallback(
    async (columnName: string) => {
      if (!sessionId) return;
      try {
        // `tags` is edited as a comma-separated string in the input but the
        // wire contract (and display code) expects an array.
        const { tags, ...rest } = editFields;
        const payload: Record<string, unknown> = { ...rest };
        if (tags !== undefined) {
          payload.tags = tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        }
        const res = await api.metadataUpdateColumn(
          sessionId,
          columnName,
          payload,
        );
        setColumnsMetadata((prev) =>
          prev.map((c) =>
            c.column_name === columnName ? { ...c, ...res.column } : c,
          ),
        );
        setEditingCol(null);
        setEditFields({});
      } catch (e: any) {
        setError(e.message);
      }
    },
    [sessionId, editFields],
  );

  // ── Publish ──
  const handlePublish = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setLoadingMsg("Publishing metadata catalog...");
    try {
      const res = await api.metadataPublish(sessionId);
      setColumnsMetadata(res.columns_metadata);
      if (res.glossary_entries) setGlossaryEntries(res.glossary_entries);
      setStep(5);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMsg("");
    }
  }, [sessionId]);

  // ── Load saved sessions on mount ──
  useEffect(() => {
    api
      .metadataListSessions()
      .then((res) => {
        setSavedSessions(res.sessions || []);
      })
      .catch(() => {});
    // Load supported databases for DDL generator
    api
      .metadataSupportedDatabases()
      .then((res) => {
        setSupportedDbs(res.databases || res);
      })
      .catch(() => {});
  }, []);

  // ── Load project DB connections for the database picker ──
  useEffect(() => {
    if (!projectId || connectionsLoaded) return;
    apiv2
      .getSharedConfig(projectId)
      .then((cfg) => {
        const conns: {
          label: string;
          type: "source" | "target";
          config: DBConfigOut;
        }[] = [];
        if (cfg.source_db) {
          conns.push({
            label: `Source DB — ${cfg.source_db.db_type} · ${cfg.source_db.database || cfg.source_db.host}`,
            type: "source",
            config: cfg.source_db,
          });
        }
        if (cfg.target_db) {
          conns.push({
            label: `Target DB — ${cfg.target_db.db_type} · ${cfg.target_db.database || cfg.target_db.host}`,
            type: "target",
            config: cfg.target_db,
          });
        }
        setProjectDbConnections(conns);
        if (conns.length > 0) setSelectedConnectionType(conns[0].type);
        setConnectionsLoaded(true);
      })
      .catch(() => setConnectionsLoaded(true));
  }, [projectId, connectionsLoaded]);

  // ── Auto-resume session when navigated from project tile ──
  useEffect(() => {
    if (resumeSessionId) {
      handleResumeSession(resumeSessionId);
    }
  }, [resumeSessionId]);

  // ── Generate DDL (from the profiled source's own native types) ──
  const handleGenerateDDL = useCallback(async () => {
    if (!sessionId) return;
    if (ddlTableMode === "selected" && ddlSelectedTables.length === 0) return;
    setDdlLoading(true);
    setDdlResult(null);
    try {
      const res = await api.metadataGenerateDDL({
        session_id: sessionId,
        table_names: ddlTableMode === "all" ? [] : ddlSelectedTables,
        db_type: ddlDb,
        ddl_type: ddlType,
        schema_name: ddlSchemaName || undefined,
      });
      setDdlResult(res);
    } catch (e: any) {
      setError(e.message || "DDL generation failed");
    } finally {
      setDdlLoading(false);
    }
  }, [
    sessionId,
    ddlTableMode,
    ddlSelectedTables,
    ddlDb,
    ddlType,
    ddlSchemaName,
  ]);

  const handleCopyDDL = useCallback(() => {
    if (!ddlResult?.ddl) return;
    navigator.clipboard.writeText(ddlResult.ddl);
    setDdlCopied(true);
    setTimeout(() => setDdlCopied(false), 2000);
  }, [ddlResult]);

  const handleDownloadDDL = useCallback(() => {
    if (!ddlResult?.ddl) return;
    const blob = new Blob([ddlResult.ddl], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ddlResult.db_type}_${ddlResult.ddl_type}.sql`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [ddlResult]);

  const toggleDdlTable = useCallback((tableName: string) => {
    setDdlSelectedTables((prev) =>
      prev.includes(tableName)
        ? prev.filter((t) => t !== tableName)
        : [...prev, tableName],
    );
  }, []);

  // ── Resume a saved session ──
  const handleResumeSession = useCallback(async (sid: string) => {
    setResumeLoading(sid);
    try {
      const res = await api.metadataResumeSession(sid);
      setSessionId(res.session_id);
      setSourceName(res.source_name || "");
      setProfile(res.profile || null);
      setColumnsMetadata(res.columns_metadata || []);
      setClarifications(res.clarifications || []);
      setChatHistory(res.chat_history || []);
      setGlossaryMarkdown(res.glossary_markdown || "");
      setLineageMermaid(res.lineage_mermaid || "");
      // Build glossary entries from columns if not stored
      if (res.glossary_entries) setGlossaryEntries(res.glossary_entries);
      if ((res as any).dataset_summary)
        setDatasetSummary((res as any).dataset_summary);
      if ((res as any).table_descriptions)
        setTableDescriptions((res as any).table_descriptions);

      // Multi-sheet session that hasn't selected sheets yet — show sheet selection
      if (
        (res as any).is_multi_sheet &&
        Array.isArray((res as any).sheets) &&
        (res as any).sheets.length > 0 &&
        (res.step === 1 || !res.columns_metadata?.length)
      ) {
        setPendingSheets((res as any).sheets);
        setShowSheetSelect(true);
        setStep(1);
      } else {
        setStep(res.step || 2);
      }
      setShowSavedSessions(false);
    } catch (e: any) {
      setError(e.message || "Failed to resume session");
    } finally {
      setResumeLoading(null);
    }
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // Filtered columns for review table
  const filteredColumns = columnsMetadata.filter((c) => {
    if (
      searchTerm &&
      !c.column_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !c.description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !c.business_term?.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    if (
      searchTable &&
      !c.table_name?.toLowerCase().includes(searchTable.toLowerCase())
    )
      return false;
    if (filterSensitivity !== "all" && c.sensitivity !== filterSensitivity)
      return false;
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    if (filterSheet !== "all" && c.table_name !== filterSheet) return false;
    return true;
  });

  // Unique sheet/table names for grouping
  const sheetNames = [...new Set(columnsMetadata.map((c) => c.table_name))];
  const isMultiSheet = sheetNames.length > 1;

  // Group columns by sheet
  const groupedBySheet = (cols: MetadataColumnEntry[]) => {
    const groups: Record<string, MetadataColumnEntry[]> = {};
    for (const c of cols) {
      const key = c.table_name || "default";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  };

  const toggleSheet = (sheet: string) => {
    setExpandedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(sheet)) next.delete(sheet);
      else next.add(sheet);
      return next;
    });
  };

  // Expand + scroll to a specific table's section ("jump to table").
  const jumpToTable = useCallback((table: string) => {
    if (!table) return;
    setExpandedSheets((prev) => new Set(prev).add(table));
    sheetRefs.current[table]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // Auto-expand all sheets on first load
  useEffect(() => {
    if (isMultiSheet && expandedSheets.size === 0) {
      setExpandedSheets(new Set(sheetNames));
    }
  }, [isMultiSheet, sheetNames.length]);

  // Stats
  const stats = {
    total: columnsMetadata.length,
    draft: columnsMetadata.filter((c) => c.status === "draft").length,
    enriched: columnsMetadata.filter((c) => c.status === "enriched").length,
    approved: columnsMetadata.filter((c) => c.status === "approved").length,
    published: columnsMetadata.filter((c) => c.status === "published").length,
    pii: columnsMetadata.filter((c) => c.sensitivity === "PII").length,
    confidential: columnsMetadata.filter(
      (c) => c.sensitivity === "Confidential",
    ).length,
    avgConfidence:
      columnsMetadata.length > 0
        ? Math.round(
            (columnsMetadata.reduce((s, c) => s + (c.confidence || 0), 0) /
              columnsMetadata.length) *
              100,
          )
        : 0,
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  if (searchParams.get("tab") === "history" && projectId) {
    return (
      <AgentSessionHistoryView
        projectId={projectId}
        sessionType="metadata"
        agentLabel="Metadata Curator"
        onResume={(id) => navigate(`?session=${id}`)}
        onNewSession={() => navigate("?")}
      />
    );
  }

  return (
    <div className="min-h-full bg-surface-500 relative">
      <div className="absolute inset-0 dot-grid pointer-events-none" />
      <div className="glow-orb glow-orb-1 pointer-events-none" />
      <div className="glow-orb glow-orb-2 pointer-events-none" />
      <GreenTendrils />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all hover:bg-brand-500/5"
            style={{ background: "rgba(28,28,42,0.5)" }}
          >
            <ArrowLeft size={18} className="text-white/50" />
          </button>
          <div className="flex-1">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-2"
              style={{ background: "rgba(134,188,37,0.08)" }}
            >
              <BookMarked size={12} />
              Data Governance
            </div>
            <h1 className="text-3xl font-black tracking-tight">
              <span className="gradient-text-green">Metadata</span>
              <span className="text-white/40 font-extralight"> Curator</span>
            </h1>
            <p className="text-sm text-white/40 mt-1">
              AI-powered metadata discovery, enrichment & governance — BMAD
              methodology
            </p>
          </div>
          {sessionId && (
            <div className="text-right text-[11px] text-white/20 max-w-[220px] shrink-0">
              <div>Session: {sessionId.slice(0, 8)}...</div>
              {sourceName && (
                <div className="text-white/30 truncate" title={sourceName}>
                  {sourceName}
                </div>
              )}
            </div>
          )}
        </div>

        <StepIndicator current={step} />

        {/* Error banner */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> {error}
            </span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs"
            >
              dismiss
            </button>
          </div>
        )}

        {/* Inline loading indicator — matches other agents */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <Loader2 className="w-10 h-10 text-brand-500 animate-spin mx-auto" />
              <p className="text-sm text-white/50">
                {loadingMsg || "Processing..."}
              </p>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* STEP 1 — Upload / Connect */}
        {/* ═══════════════════════════════════════════════════ */}
        {step === 1 && !loading && !showSheetSelect && !showDbConnect && (
          <div className="space-y-6 animate-fade-in">
            {/* Saved sessions — Continue Previous */}
            {savedSessions.length > 0 && (
              <div
                className="rounded-2xl border border-white/8 p-5"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <button
                  onClick={() => setShowSavedSessions(!showSavedSessions)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-white/80"
                >
                  <span className="flex items-center gap-2">
                    <RefreshCw size={14} className="text-brand-400" />
                    Continue Previous Session ({savedSessions.length})
                  </span>
                  {showSavedSessions ? (
                    <ChevronUp size={14} className="text-white/30" />
                  ) : (
                    <ChevronDown size={14} className="text-white/30" />
                  )}
                </button>
                {showSavedSessions && (
                  <div className="mt-3 space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                    {savedSessions.map((sess) => {
                      const stepLabel: Record<string, string> = {
                        uploaded: "Uploaded",
                        profiled: "Discovered",
                        enriched: "Enriched",
                        enriching: "Enriching",
                        clarifying: "Clarifying",
                        approved: "Approved",
                        published: "Published",
                      };
                      return (
                        <div
                          key={sess.session_id}
                          className="flex items-center justify-between rounded-xl border border-white/5 px-4 py-3 hover:border-brand-500/30 hover:bg-white/[0.02] transition-all cursor-pointer group"
                          onClick={() => handleResumeSession(sess.session_id)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-white/70 truncate">
                              {sess.source_name}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-white/25">
                                {sess.source_type}
                              </span>
                              <span className="text-[11px] text-white/15">
                                •
                              </span>
                              <span className="text-[11px] text-white/25">
                                {sess.column_count} cols
                              </span>
                              <span className="text-[11px] text-white/15">
                                •
                              </span>
                              <span
                                className={`text-[11px] px-1.5 py-0.5 rounded ${
                                  sess.step === "published"
                                    ? "bg-green-500/10 text-green-400"
                                    : sess.step === "approved"
                                      ? "bg-blue-500/10 text-blue-400"
                                      : "bg-white/5 text-white/30"
                                }`}
                              >
                                {stepLabel[sess.step] || sess.step}
                              </span>
                              {sess.published_count > 0 && (
                                <span className="text-[11px] text-green-400/60">
                                  {sess.published_count} published
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-white/15 mt-0.5">
                              {new Date(sess.created_at).toLocaleDateString()}{" "}
                              {new Date(sess.created_at).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" },
                              )}
                            </div>
                          </div>
                          <div className="ml-3">
                            {resumeLoading === sess.session_id ? (
                              <Loader2
                                size={14}
                                className="text-brand-400 animate-spin"
                              />
                            ) : (
                              <ArrowRight
                                size={14}
                                className="text-white/20 group-hover:text-brand-400 transition-colors"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Glossary mode selection */}
            <div
              className="rounded-2xl border border-white/8 p-5"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <h3 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
                <FileText size={14} className="text-brand-400" /> Business
                Glossary
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={() => setGlossaryMode("generate")}
                  className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                    glossaryMode === "generate"
                      ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                      : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                  }`}
                >
                  <div className="text-xs font-semibold">
                    Generate New Glossary
                  </div>
                  <div className="text-[11px] mt-1 opacity-60">
                    AI creates business terms from your data
                  </div>
                </button>
                <button
                  onClick={() => setGlossaryMode("update")}
                  className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                    glossaryMode === "update"
                      ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                      : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                  }`}
                >
                  <div className="text-xs font-semibold">
                    Update Existing Glossary
                  </div>
                  <div className="text-[11px] mt-1 opacity-60">
                    Map columns to your existing business terms
                  </div>
                </button>
              </div>
            </div>

            {/* Source type toggle */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setSourceType("file");
                  setShowDbConnect(false);
                }}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all ${
                  sourceType === "file"
                    ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                    : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                }`}
              >
                <Upload size={16} /> File Upload
              </button>
              <button
                data-testid="metadata-source-database"
                onClick={() => {
                  setSourceType("database");
                  setShowDbConnect(true);
                }}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all ${
                  sourceType === "database"
                    ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                    : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                }`}
              >
                <Database size={16} /> Database Connection
              </button>
            </div>

            {/* File upload area */}
            {sourceType === "file" && (
              <div
                className={`rounded-2xl border-2 border-dashed p-12 text-center transition-all cursor-pointer ${
                  dragOver
                    ? "border-brand-500/60 bg-brand-500/5"
                    : "border-white/10 hover:border-brand-500/30 hover:bg-white/2"
                }`}
                style={{
                  background: dragOver ? undefined : "rgba(28,28,42,0.3)",
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleFileUpload(file);
                }}
              >
                <Upload size={40} className="mx-auto text-white/20 mb-4" />
                <p className="text-white/60 text-sm mb-2">
                  Drop a file here or{" "}
                  <span className="text-brand-400">browse</span>
                </p>
                <p className="text-white/25 text-xs">
                  CSV, Excel, JSON, Parquet — up to 100MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv,.tsv,.txt,.xlsx,.xls,.json,.jsonl,.parquet"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Sheet selection overlay */}
        {showSheetSelect && !loading && pendingSheets.length > 0 && (
          <div className="space-y-4 animate-fade-in">
            <div
              className="rounded-2xl border border-white/8 p-6"
              style={{ background: "rgba(28,28,42,0.6)" }}
            >
              <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-brand-400" />
                Select Sheets to Catalog
                <button
                  type="button"
                  onClick={() => {
                    if (selectedSheets.length === pendingSheets.length) {
                      setSelectedSheets([]);
                    } else {
                      setSelectedSheets(pendingSheets.map((s) => s.name));
                    }
                  }}
                  className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded border border-white/10 text-white/50 hover:text-white/80 hover:border-brand-500/40 transition-colors"
                >
                  {selectedSheets.length === pendingSheets.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </h3>
              <div className="space-y-2 mb-4">
                {pendingSheets.map((sheet) => (
                  <label
                    key={sheet.name}
                    className="flex items-center gap-3 p-3 rounded-xl border border-white/8 hover:border-brand-500/30 cursor-pointer transition-all"
                    style={{
                      background: selectedSheets.includes(sheet.name)
                        ? "rgba(134,188,37,0.05)"
                        : "rgba(28,28,42,0.3)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSheets.includes(sheet.name)}
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedSheets((prev) => [...prev, sheet.name]);
                        else
                          setSelectedSheets((prev) =>
                            prev.filter((s) => s !== sheet.name),
                          );
                      }}
                      className="accent-brand-500"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-medium text-white/70">
                        {sheet.name}
                      </div>
                      <div className="text-[11px] text-white/30">
                        {sheet.row_count} rows · {sheet.col_count} columns
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={handleSheetSelect}
                disabled={selectedSheets.length === 0}
                className="px-6 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Analyze Selected Sheets
              </button>
            </div>
          </div>
        )}

        {/* DB Connect — uses saved project connections */}
        {showDbConnect &&
          !loading &&
          sourceType === "database" &&
          step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div
                className="rounded-2xl border border-white/8 p-6"
                style={{ background: "rgba(28,28,42,0.6)" }}
              >
                <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                  <Database size={14} className="text-brand-400" />
                  Database Connection
                </h3>

                {/* Already connected — tables/views loaded — show a compact summary
                    instead of the connect prompt below, to avoid showing both at once. */}
                {dbTables.length > 0 || dbViews.length > 0 ? (
                  <div className="flex items-center justify-between rounded-xl border border-brand-500/20 bg-brand-500/5 px-4 py-2.5">
                    <span className="flex items-center gap-2 text-xs text-brand-400">
                      <Check size={14} /> Connected — browsing tables & views
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setDbTables([]);
                        setDbViews([]);
                        setSelectedDbObjects(new Set());
                        setDbSearchTerm("");
                      }}
                      className="text-[11px] text-white/40 hover:text-white/70 transition-colors"
                    >
                      Change connection
                    </button>
                  </div>
                ) : /* Project has saved connections */
                projectDbConnections.length > 0 ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-[11px] text-white/40 mb-1.5 block">
                        Select Connection
                      </label>
                      <div className="flex gap-2">
                        {projectDbConnections.map((conn) => (
                          <button
                            key={conn.type}
                            onClick={() => {
                              setSelectedConnectionType(conn.type);
                              setDbTables([]);
                              setDbViews([]);
                              setSelectedDbObjects(new Set());
                              setDbSearchTerm("");
                            }}
                            className={`flex-1 flex items-center gap-2.5 px-4 py-3 rounded-xl border text-xs font-medium transition-all ${
                              selectedConnectionType === conn.type
                                ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                                : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                            }`}
                          >
                            <Database size={14} />
                            <div className="text-left">
                              <div className="font-semibold">
                                {conn.type === "source" ? "Source" : "Target"}{" "}
                                Database
                              </div>
                              <div className="text-[11px] opacity-60 mt-0.5">
                                {conn.config.db_type} ·{" "}
                                {conn.config.database || conn.config.host}
                                {conn.config.schema_name
                                  ? ` · ${conn.config.schema_name}`
                                  : ""}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        data-testid="metadata-db-connect-btn"
                        onClick={handleDbConnect}
                        className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        List Tables & Views
                      </button>
                      <button
                        type="button"
                        data-testid="metadata-open-saved-connections"
                        onClick={() => setShowSharedConnectDialog(true)}
                        className="text-[11px] text-white/40 hover:text-white/60 transition-colors underline"
                      >
                        Or pick a connection saved by another agent…
                      </button>
                    </div>

                    {/* Divider to manual connect */}
                    <div className="flex items-center gap-3 pt-2">
                      <div className="flex-1 h-px bg-white/5" />
                      <button
                        type="button"
                        data-testid="metadata-open-manual-connect"
                        onClick={() => setShowManualDbModal(true)}
                        className="text-[11px] text-white/50 hover:text-white/70 transition-colors"
                      >
                        Or connect manually
                      </button>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                  </div>
                ) : connectionsLoaded ? (
                  /* No project connections — Data Profiler-style empty state */
                  <div className="flex flex-col items-center text-center py-8">
                    <div className="w-14 h-14 rounded-full bg-brand-500/10 flex items-center justify-center mb-4">
                      <Database size={24} className="text-brand-400" />
                    </div>
                    <p className="text-sm font-semibold text-white/70 mb-1">
                      Connect a database to browse tables & views
                    </p>
                    <p className="text-xs text-white/30 mb-5 max-w-sm">
                      Set up a Source or Target database in{" "}
                      <span className="text-brand-400">Project Settings</span>,
                      or connect directly below.
                    </p>
                    <button
                      data-testid="metadata-open-manual-connect-empty"
                      onClick={() => setShowManualDbModal(true)}
                      className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Database size={16} /> Connect Database
                    </button>
                  </div>
                ) : (
                  /* Loading connections */
                  <div className="flex items-center gap-2 py-4">
                    <Loader2
                      size={14}
                      className="animate-spin text-brand-400"
                    />
                    <span className="text-xs text-white/40">
                      Loading project connections...
                    </span>
                  </div>
                )}

                {/* Tables/Views picker — shared across project & manual connect modes */}
                {(dbTables.length > 0 || dbViews.length > 0) &&
                  (() => {
                    const searchLower = dbSearchTerm.toLowerCase();
                    const filteredTables = dbTables.filter((t) =>
                      t.toLowerCase().includes(searchLower),
                    );
                    const filteredViews = dbViews.filter((v) =>
                      v.toLowerCase().includes(searchLower),
                    );
                    const visibleItems = [
                      ...(dbObjectFilter === "all" ||
                      dbObjectFilter === "tables"
                        ? filteredTables
                        : []),
                      ...(dbObjectFilter === "all" || dbObjectFilter === "views"
                        ? filteredViews
                        : []),
                    ];
                    const allVisibleSelected =
                      visibleItems.length > 0 &&
                      visibleItems.every((i) => selectedDbObjects.has(i));

                    return (
                      <div className="mt-4 space-y-3">
                        {/* Search + filter tabs */}
                        <div className="flex items-center gap-3">
                          <div className="relative flex-1">
                            <Search
                              size={13}
                              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/25"
                            />
                            <input
                              type="text"
                              value={dbSearchTerm}
                              onChange={(e) => setDbSearchTerm(e.target.value)}
                              placeholder="Search tables & views..."
                              className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/80 placeholder:text-white/25 focus:border-brand-500/40 focus:outline-none transition-colors"
                            />
                            {dbSearchTerm && (
                              <button
                                onClick={() => setDbSearchTerm("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/50"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {(["all", "tables", "views"] as const).map((f) => (
                              <button
                                key={f}
                                onClick={() => setDbObjectFilter(f)}
                                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                                  dbObjectFilter === f
                                    ? "bg-brand-500/20 text-brand-400 border border-brand-500/30"
                                    : "bg-white/3 text-white/30 border border-white/5 hover:border-white/10"
                                }`}
                              >
                                {f === "all"
                                  ? `All (${filteredTables.length + filteredViews.length})`
                                  : f === "tables"
                                    ? `Tables (${filteredTables.length})`
                                    : `Views (${filteredViews.length})`}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Select all + selection count */}
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <button
                              onClick={() =>
                                handleToggleAllVisible(visibleItems)
                              }
                              className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                allVisibleSelected
                                  ? "bg-brand-500 border-brand-500 text-white"
                                  : "border-white/20 bg-white/5 group-hover:border-white/30"
                              }`}
                            >
                              {allVisibleSelected && <Check size={10} />}
                            </button>
                            <span className="text-[11px] text-white/40 group-hover:text-white/60">
                              Select all visible ({visibleItems.length})
                            </span>
                          </label>
                          {selectedDbObjects.size > 0 && (
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-brand-400 font-medium">
                                {selectedDbObjects.size} selected
                              </span>
                              <button
                                onClick={() => setSelectedDbObjects(new Set())}
                                className="text-[11px] text-white/30 hover:text-white/50 underline"
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Grid of table/view cards with checkboxes */}
                        <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto custom-scrollbar">
                          {(dbObjectFilter === "all" ||
                            dbObjectFilter === "tables") &&
                            filteredTables.map((t) => {
                              const selected = selectedDbObjects.has(t);
                              return (
                                <button
                                  key={`t-${t}`}
                                  data-testid="metadata-table-option"
                                  data-object={t}
                                  onClick={() => handleToggleDbObject(t)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all text-left ${
                                    selected
                                      ? "bg-brand-500/15 border border-brand-500/40 text-brand-300"
                                      : "bg-white/5 border border-white/10 text-white/60 hover:border-brand-500/30 hover:text-white/80"
                                  }`}
                                >
                                  <div
                                    className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                                      selected
                                        ? "bg-brand-500 border-brand-500 text-white"
                                        : "border-white/20 bg-white/5"
                                    }`}
                                  >
                                    {selected && <Check size={9} />}
                                  </div>
                                  <Table2
                                    size={12}
                                    className={`flex-shrink-0 ${selected ? "text-brand-400" : "text-blue-400/60"}`}
                                  />
                                  <span className="truncate">{t}</span>
                                </button>
                              );
                            })}
                          {(dbObjectFilter === "all" ||
                            dbObjectFilter === "views") &&
                            filteredViews.map((v) => {
                              const selected = selectedDbObjects.has(v);
                              return (
                                <button
                                  key={`v-${v}`}
                                  onClick={() => handleToggleDbObject(v)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all text-left ${
                                    selected
                                      ? "bg-brand-500/15 border border-brand-500/40 text-brand-300"
                                      : "bg-white/5 border border-white/10 text-white/60 hover:border-brand-500/30 hover:text-white/80"
                                  }`}
                                >
                                  <div
                                    className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                                      selected
                                        ? "bg-brand-500 border-brand-500 text-white"
                                        : "border-white/20 bg-white/5"
                                    }`}
                                  >
                                    {selected && <Check size={9} />}
                                  </div>
                                  <Eye
                                    size={12}
                                    className={`flex-shrink-0 ${selected ? "text-brand-400" : "text-purple-400/60"}`}
                                  />
                                  <span className="truncate">{v}</span>
                                </button>
                              );
                            })}
                        </div>

                        {visibleItems.length === 0 && dbSearchTerm && (
                          <div className="text-center py-4">
                            <p className="text-xs text-white/30">
                              No tables or views matching "{dbSearchTerm}"
                            </p>
                          </div>
                        )}

                        {/* Profile Selected button */}
                        {selectedDbObjects.size > 0 && (
                          <button
                            data-testid="metadata-profile-selected-btn"
                            onClick={handleProfileSelectedTables}
                            className="w-full px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <Sparkles size={14} />
                            Discover {selectedDbObjects.size} Selected Table
                            {selectedDbObjects.size > 1 ? "s" : ""}
                          </button>
                        )}
                      </div>
                    );
                  })()}
              </div>
            </div>
          )}

        {/* Manual "Connect Database" dialog — mirrors Data Profiler's modal */}
        {showManualDbModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowManualDbModal(false);
            }}
            role="dialog"
            aria-modal="true"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleManualDbConnect();
              }}
              className="rounded-2xl border border-white/8 p-6 max-w-lg w-full mx-4 space-y-4"
              style={{ background: "rgba(28,28,42,0.95)" }}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2">
                  <Database size={14} className="text-brand-400" />
                  Connect Database
                </h3>
                <button
                  type="button"
                  onClick={() => setShowManualDbModal(false)}
                  className="p-1 rounded hover:bg-white/5"
                >
                  <X size={16} className="text-white/40" />
                </button>
              </div>

              <div>
                <label className="text-[11px] text-white/40 mb-1.5 block">
                  Database Type
                </label>
                <select
                  aria-label="Database Type"
                  data-testid="metadata-db-type"
                  value={dbConfig.db_type}
                  onChange={(e) => {
                    const nextDbType = e.target.value;
                    setDbConfig((p) => {
                      // Only auto-fill the port if it's still at the previous
                      // engine's default (or unset) — don't clobber a port
                      // the user has manually edited (e.g. a tunnel port).
                      const isUntouched =
                        !p.port ||
                        (p.db_type !== undefined &&
                          p.port === DEFAULT_DB_PORTS[p.db_type]);
                      return {
                        ...p,
                        db_type: nextDbType,
                        port: isUntouched
                          ? (DEFAULT_DB_PORTS[nextDbType] ?? p.port)
                          : p.port,
                      };
                    });
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
                >
                  <option value="postgresql">PostgreSQL</option>
                  <option value="mysql">MySQL</option>
                  <option value="mssql">SQL Server</option>
                  <option value="oracle">Oracle</option>
                  <option value="snowflake">Snowflake</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Host
                  </label>
                  <input
                    aria-label="Host"
                    data-testid="metadata-db-host"
                    value={dbConfig.host}
                    onChange={(e) =>
                      setDbConfig((p) => ({ ...p, host: e.target.value }))
                    }
                    placeholder="localhost"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Port
                  </label>
                  <input
                    type="number"
                    aria-label="Port"
                    data-testid="metadata-db-port"
                    value={dbConfig.port ?? ""}
                    onChange={(e) =>
                      setDbConfig((p) => ({
                        ...p,
                        port: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder="5432"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-white/40 mb-1.5 block">
                  Database
                </label>
                <input
                  aria-label="Database"
                  data-testid="metadata-db-database"
                  value={dbConfig.database}
                  onChange={(e) =>
                    setDbConfig((p) => ({ ...p, database: e.target.value }))
                  }
                  placeholder="my_database"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Username
                  </label>
                  <input
                    aria-label="Username"
                    data-testid="metadata-db-username"
                    value={dbConfig.username}
                    onChange={(e) =>
                      setDbConfig((p) => ({ ...p, username: e.target.value }))
                    }
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Password
                  </label>
                  <input
                    type="password"
                    aria-label="Password"
                    data-testid="metadata-db-password"
                    value={dbConfig.password}
                    onChange={(e) =>
                      setDbConfig((p) => ({ ...p, password: e.target.value }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Schema (optional)
                  </label>
                  <input
                    aria-label="Schema"
                    data-testid="metadata-db-schema"
                    value={dbConfig.schema_name || ""}
                    onChange={(e) =>
                      setDbConfig((p) => ({
                        ...p,
                        schema_name: e.target.value || undefined,
                      }))
                    }
                    placeholder="public"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-white/40 mb-1.5 block">
                    Table (optional)
                  </label>
                  <input
                    aria-label="Table"
                    data-testid="metadata-db-table"
                    value={dbConfig.table || ""}
                    onChange={(e) =>
                      setDbConfig((p) => ({
                        ...p,
                        table: e.target.value || undefined,
                      }))
                    }
                    placeholder="Leave blank to list all"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/25"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="metadata-db-connect-btn-manual"
                className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Database size={16} /> Connect
              </button>
            </form>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* STEP 2 — Auto-Discover Summary */}
        {/* ═══════════════════════════════════════════════════ */}
        {step === 2 && !loading && profile && (
          <div className="space-y-6 animate-fade-in">
            {/* Summary cards */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  ...(isMultiSheet
                    ? [
                        {
                          label:
                            sourceType === "database" ? "Tables" : "Sheets",
                          value: `${sheetNames.length}`,
                          icon: FileSpreadsheet,
                        },
                      ]
                    : []),
                  { label: "Columns", value: `${stats.total}`, icon: Table2 },
                  {
                    label: "Rows",
                    value: (profile?.metadata?.rows ?? 0).toLocaleString(),
                    icon: BarChart3,
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 rounded-lg border border-white/8 px-3 py-1.5"
                    style={{ background: "rgba(28,28,42,0.5)" }}
                  >
                    <card.icon size={12} className="text-white/30" />
                    <span className="text-sm font-bold text-white/80">
                      {card.value}
                    </span>
                    <span className="text-[11px] text-white/30">
                      {card.label}
                    </span>
                  </div>
                ))}
                <button
                  onClick={() =>
                    setStatsFocus((prev) => (prev === "pii" ? null : "pii"))
                  }
                  title="Click to see which columns were flagged as PII"
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 transition-all ${
                    statsFocus === "pii"
                      ? "border-red-500/40 bg-red-500/10"
                      : "border-white/8 hover:border-red-500/30"
                  }`}
                  style={
                    statsFocus === "pii"
                      ? undefined
                      : { background: "rgba(28,28,42,0.5)" }
                  }
                >
                  <ShieldCheck size={12} className="text-white/30" />
                  <span className="text-sm font-bold text-white/80">
                    {stats.pii}
                  </span>
                  <span className="text-[11px] text-white/30">
                    PII Detected
                  </span>
                </button>
                <button
                  onClick={() =>
                    setStatsFocus((prev) =>
                      prev === "confidential" ? null : "confidential",
                    )
                  }
                  title="Click to see which columns were flagged as Confidential"
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 transition-all ${
                    statsFocus === "confidential"
                      ? "border-orange-500/40 bg-orange-500/10"
                      : "border-white/8 hover:border-orange-500/30"
                  }`}
                  style={
                    statsFocus === "confidential"
                      ? undefined
                      : { background: "rgba(28,28,42,0.5)" }
                  }
                >
                  <Lock size={12} className="text-white/30" />
                  <span className="text-sm font-bold text-white/80">
                    {stats.confidential}
                  </span>
                  <span className="text-[11px] text-white/30">
                    Confidential
                  </span>
                </button>
              </div>

              {isMultiSheet && (
                <select
                  value=""
                  onChange={(e) => jumpToTable(e.target.value)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white/60 outline-none focus:border-brand-500/40"
                >
                  <option value="">
                    Jump to {sourceType === "database" ? "table" : "sheet"}
                    ...
                  </option>
                  {sheetNames.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* PII / Confidential breakdown — shown on demand, click a column
                to jump straight to its table below */}
            {statsFocus && (
              <div
                className="rounded-xl border border-white/8 p-4 space-y-2"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-white/60 flex items-center gap-1.5">
                    {statsFocus === "pii" ? (
                      <ShieldCheck size={12} className="text-red-400" />
                    ) : (
                      <Lock size={12} className="text-orange-400" />
                    )}
                    {statsFocus === "pii" ? "PII" : "Confidential"} columns
                  </h4>
                  <button
                    onClick={() => setStatsFocus(null)}
                    className="text-white/20 hover:text-white/50"
                  >
                    <X size={12} />
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-1">
                  {columnsMetadata
                    .filter(
                      (c) =>
                        c.sensitivity ===
                        (statsFocus === "pii" ? "PII" : "Confidential"),
                    )
                    .map((c, i) => (
                      <button
                        key={i}
                        onClick={() => jumpToTable(c.table_name)}
                        className="w-full flex items-center justify-between text-left text-[11px] px-2 py-1 rounded hover:bg-white/5 transition-all"
                      >
                        <span className="font-mono text-white/70">
                          {c.column_name}
                        </span>
                        <span className="text-white/30">{c.table_name}</span>
                      </button>
                    ))}
                  {columnsMetadata.filter(
                    (c) =>
                      c.sensitivity ===
                      (statsFocus === "pii" ? "PII" : "Confidential"),
                  ).length === 0 && (
                    <p className="text-[11px] text-white/25 px-2 py-1">
                      No columns flagged.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Actions — kept at the top so they're reachable without
                scrolling past every table's detail */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-5 py-2.5 border border-white/10 text-white/50 rounded-xl text-sm hover:border-white/20 transition-all"
              >
                <ArrowLeft size={14} className="inline mr-1" /> Back
              </button>
              <button
                data-testid="metadata-enrich-btn"
                onClick={handleEnrich}
                className="px-8 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Sparkles size={14} /> Enrich with AI
              </button>
            </div>

            {/* Draft metadata — grouped by sheet if multi-sheet */}
            {isMultiSheet ? (
              <div className="space-y-4">
                {Object.entries(groupedBySheet(columnsMetadata)).map(
                  ([sheet, cols]) => {
                    const isExpanded = expandedSheets.has(sheet);
                    const sheetPii = cols.filter(
                      (c) => c.sensitivity === "PII",
                    ).length;
                    const sheetConfidential = cols.filter(
                      (c) => c.sensitivity === "Confidential",
                    ).length;
                    return (
                      <div
                        key={sheet}
                        ref={(el) => {
                          sheetRefs.current[sheet] = el;
                        }}
                        className="rounded-2xl border border-white/8 overflow-hidden"
                        style={{ background: "rgba(28,28,42,0.5)" }}
                      >
                        <div
                          onClick={() => toggleSheet(sheet)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              toggleSheet(sheet);
                            }
                          }}
                          className="w-full px-5 py-3 border-b border-white/5 flex items-center justify-between hover:bg-white/2 transition-all cursor-pointer"
                        >
                          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown
                                size={14}
                                className="text-brand-400"
                              />
                            ) : (
                              <ChevronRight
                                size={14}
                                className="text-white/30"
                              />
                            )}
                            <FileSpreadsheet
                              size={14}
                              className="text-brand-400"
                            />
                            {sheet}
                            <span className="text-[11px] text-white/30 font-normal">
                              ({cols.length} columns)
                            </span>
                          </h3>
                          <div className="flex items-center gap-3 text-[11px]">
                            {sheetPii > 0 && (
                              <span className="text-red-400 flex items-center gap-1">
                                <Shield size={10} />
                                {sheetPii} PII
                              </span>
                            )}
                            {sheetConfidential > 0 && (
                              <span className="text-orange-400 flex items-center gap-1">
                                <Lock size={10} />
                                {sheetConfidential} Confidential
                              </span>
                            )}
                            <span className="text-white/25">
                              {cols.filter((c) => c.null_pct > 50).length}{" "}
                              high-null
                            </span>
                            <TableExportButtons
                              sessionId={sessionId}
                              tableName={sheet}
                            />
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="overflow-x-auto max-h-[350px] overflow-y-auto custom-scrollbar">
                            <table className="w-full text-xs">
                              <thead className="bg-white/3 sticky top-0 z-10">
                                <tr className="text-white/40 text-left">
                                  <th className="px-4 py-2">Column</th>
                                  <th className="px-4 py-2">Type</th>
                                  <th className="px-4 py-2">Domain</th>
                                  <th className="px-4 py-2">Sensitivity</th>
                                  <th className="px-4 py-2">Business Term</th>
                                  <th className="px-4 py-2">Confidence</th>
                                  <th className="px-4 py-2">Null%</th>
                                  <th className="px-4 py-2 w-16"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {cols.map((col, i) => (
                                  <tr
                                    key={i}
                                    className="border-t border-white/3 hover:bg-white/2"
                                  >
                                    <td className="px-4 py-2 font-mono text-white/70">
                                      {col.column_name}
                                      <KeyBadges col={col} />
                                    </td>
                                    <td className="px-4 py-2 text-white/40">
                                      {col.native_type || col.data_type}
                                    </td>
                                    <td className="px-4 py-2 text-white/50">
                                      {col.domain}
                                    </td>
                                    <td className="px-4 py-2">
                                      {editingCol === col.column_name ? (
                                        <select
                                          value={
                                            editFields.sensitivity ??
                                            col.sensitivity
                                          }
                                          onChange={(e) =>
                                            setEditFields((prev) => ({
                                              ...prev,
                                              sensitivity: e.target.value,
                                            }))
                                          }
                                          className="px-2 py-1 bg-white/5 border border-brand-500/40 rounded text-[11px] text-white/80 outline-none"
                                        >
                                          <option value="Public">Public</option>
                                          <option value="Internal">
                                            Internal
                                          </option>
                                          <option value="Confidential">
                                            Confidential
                                          </option>
                                          <option value="PII">PII</option>
                                        </select>
                                      ) : (
                                        <SensitivityBadge
                                          level={col.sensitivity}
                                        />
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      {editingCol === col.column_name ? (
                                        <input
                                          value={
                                            editFields.business_term ??
                                            (col.business_term || "")
                                          }
                                          onChange={(e) =>
                                            setEditFields((prev) => ({
                                              ...prev,
                                              business_term: e.target.value,
                                            }))
                                          }
                                          className="w-full px-2 py-1 bg-white/5 border border-brand-500/40 rounded text-[11px] text-white/80 outline-none"
                                        />
                                      ) : (
                                        <span className="text-white/50">
                                          {col.business_term}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      <ConfidenceBar value={col.confidence} />
                                    </td>
                                    <td className="px-4 py-2 text-white/40">
                                      {col.null_pct}%
                                    </td>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-1">
                                        {editingCol === col.column_name ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleSaveEdit(col.column_name)
                                              }
                                              className="p-1 rounded bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 transition-all"
                                              title="Save"
                                            >
                                              <Check size={12} />
                                            </button>
                                            <button
                                              onClick={() => {
                                                setEditingCol(null);
                                                setEditFields({});
                                              }}
                                              className="p-1 rounded bg-white/5 text-white/30 hover:bg-white/10 transition-all"
                                              title="Cancel"
                                            >
                                              <X size={12} />
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            onClick={() => {
                                              setEditingCol(col.column_name);
                                              setEditFields({});
                                            }}
                                            className="p-1 rounded hover:bg-white/5 text-white/20 hover:text-white/50 transition-all"
                                            title="Edit"
                                          >
                                            <Edit3 size={12} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              /* Single-sheet flat table */
              <div
                className="rounded-2xl border border-white/8 overflow-hidden"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
                    <BookMarked size={14} className="text-brand-400" />
                    Draft Metadata ({columnsMetadata.length} columns)
                  </h3>
                  <div className="flex items-center gap-2 text-[11px] text-white/30">
                    <span className="flex items-center gap-1">
                      <Edit3 size={10} /> Click edit to override Sensitivity or
                      Business Term
                    </span>
                    <span className="text-white/10">|</span>
                    <span className="flex items-center gap-1">
                      <Info size={10} /> Heuristic-based — click Enrich for AI
                    </span>
                    <span className="text-white/10">|</span>
                    <TableExportButtons
                      sessionId={sessionId}
                      tableName={columnsMetadata[0]?.table_name || ""}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead className="bg-white/3 sticky top-0 z-10">
                      <tr className="text-white/40 text-left">
                        <th className="px-4 py-2">Column</th>
                        <th className="px-4 py-2">Type</th>
                        <th className="px-4 py-2">Domain</th>
                        <th className="px-4 py-2">Sensitivity</th>
                        <th className="px-4 py-2">Business Term</th>
                        <th className="px-4 py-2">Confidence</th>
                        <th className="px-4 py-2">Null%</th>
                        <th className="px-4 py-2 w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {columnsMetadata.map((col, i) => (
                        <tr
                          key={i}
                          className="border-t border-white/3 hover:bg-white/2"
                        >
                          <td className="px-4 py-2 font-mono text-white/70">
                            {col.column_name}
                            <KeyBadges col={col} />
                          </td>
                          <td className="px-4 py-2 text-white/40">
                            {col.native_type || col.data_type}
                          </td>
                          <td className="px-4 py-2 text-white/50">
                            {col.domain}
                          </td>
                          <td className="px-4 py-2">
                            {editingCol === col.column_name ? (
                              <select
                                value={
                                  editFields.sensitivity ?? col.sensitivity
                                }
                                onChange={(e) =>
                                  setEditFields((prev) => ({
                                    ...prev,
                                    sensitivity: e.target.value,
                                  }))
                                }
                                className="px-2 py-1 bg-white/5 border border-brand-500/40 rounded text-[11px] text-white/80 outline-none"
                              >
                                <option value="Public">Public</option>
                                <option value="Internal">Internal</option>
                                <option value="Confidential">
                                  Confidential
                                </option>
                                <option value="PII">PII</option>
                              </select>
                            ) : (
                              <SensitivityBadge level={col.sensitivity} />
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {editingCol === col.column_name ? (
                              <input
                                value={
                                  editFields.business_term ??
                                  (col.business_term || "")
                                }
                                onChange={(e) =>
                                  setEditFields((prev) => ({
                                    ...prev,
                                    business_term: e.target.value,
                                  }))
                                }
                                className="w-full px-2 py-1 bg-white/5 border border-brand-500/40 rounded text-[11px] text-white/80 outline-none"
                              />
                            ) : (
                              <span className="text-white/50">
                                {col.business_term}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <ConfidenceBar value={col.confidence} />
                          </td>
                          <td className="px-4 py-2 text-white/40">
                            {col.null_pct}%
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              {editingCol === col.column_name ? (
                                <>
                                  <button
                                    onClick={() =>
                                      handleSaveEdit(col.column_name)
                                    }
                                    className="p-1 rounded bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 transition-all"
                                    title="Save"
                                  >
                                    <Check size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCol(null);
                                      setEditFields({});
                                    }}
                                    className="p-1 rounded bg-white/5 text-white/30 hover:bg-white/10 transition-all"
                                    title="Cancel"
                                  >
                                    <X size={12} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingCol(col.column_name);
                                    setEditFields({});
                                  }}
                                  className="p-1 rounded hover:bg-white/5 text-white/20 hover:text-white/50 transition-all"
                                  title="Edit"
                                >
                                  <Edit3 size={12} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* STEP 3 — AI Enrichment (shown as loading, transitions to step 4) */}
        {/* ═══════════════════════════════════════════════════ */}
        {step === 3 && (
          <div className="flex items-center justify-center min-h-[40vh] animate-fade-in">
            <div className="text-center space-y-4">
              <Sparkles className="w-12 h-12 text-brand-500 mx-auto animate-pulse" />
              <p className="text-lg text-white/70">AI Enrichment in Progress</p>
              <p className="text-sm text-white/30">
                Claude is generating descriptions, detecting PII, and mapping
                glossary terms...
              </p>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* STEP 4 — Clarify & Review */}
        {/* ═══════════════════════════════════════════════════ */}
        {step === 4 && !loading && (
          <div className="space-y-6 animate-fade-in">
            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {[
                { label: "Total", value: stats.total, color: "text-white/60" },
                {
                  label: "Enriched",
                  value: stats.enriched,
                  color: "text-blue-400",
                },
                {
                  label: "Approved",
                  value: stats.approved,
                  color: "text-green-400",
                },
                {
                  label: "Avg Confidence",
                  value: `${stats.avgConfidence}%`,
                  color: "text-brand-400",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-white/8 px-3 py-2 text-center"
                  style={{ background: "rgba(28,28,42,0.4)" }}
                >
                  <div className="text-[11px] text-white/30">{s.label}</div>
                  <div className={`text-lg font-bold ${s.color}`}>
                    {s.value}
                  </div>
                </div>
              ))}
              <button
                onClick={() =>
                  setStatsFocus((prev) => (prev === "pii" ? null : "pii"))
                }
                title="Click to see which columns were flagged as PII"
                className={`rounded-xl border px-3 py-2 text-center transition-all ${
                  statsFocus === "pii"
                    ? "border-red-500/40 bg-red-500/10"
                    : "border-white/8 hover:border-red-500/30"
                }`}
                style={
                  statsFocus === "pii"
                    ? undefined
                    : { background: "rgba(28,28,42,0.4)" }
                }
              >
                <div className="text-[11px] text-white/30">PII</div>
                <div className="text-lg font-bold text-red-400">
                  {stats.pii}
                </div>
              </button>
              <button
                onClick={() =>
                  setStatsFocus((prev) =>
                    prev === "confidential" ? null : "confidential",
                  )
                }
                title="Click to see which columns were flagged as Confidential"
                className={`rounded-xl border px-3 py-2 text-center transition-all ${
                  statsFocus === "confidential"
                    ? "border-orange-500/40 bg-orange-500/10"
                    : "border-white/8 hover:border-orange-500/30"
                }`}
                style={
                  statsFocus === "confidential"
                    ? undefined
                    : { background: "rgba(28,28,42,0.4)" }
                }
              >
                <div className="text-[11px] text-white/30">Confidential</div>
                <div className="text-lg font-bold text-orange-400">
                  {stats.confidential}
                </div>
              </button>
            </div>

            {/* PII / Confidential breakdown — shown on demand, click a column
                to jump straight to its table below */}
            {statsFocus && (
              <div
                className="rounded-xl border border-white/8 p-4 space-y-2"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-white/60 flex items-center gap-1.5">
                    {statsFocus === "pii" ? (
                      <ShieldCheck size={12} className="text-red-400" />
                    ) : (
                      <Lock size={12} className="text-orange-400" />
                    )}
                    {statsFocus === "pii" ? "PII" : "Confidential"} columns
                  </h4>
                  <button
                    onClick={() => setStatsFocus(null)}
                    className="text-white/20 hover:text-white/50"
                  >
                    <X size={12} />
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-1">
                  {columnsMetadata
                    .filter(
                      (c) =>
                        c.sensitivity ===
                        (statsFocus === "pii" ? "PII" : "Confidential"),
                    )
                    .map((c, i) => (
                      <button
                        key={i}
                        onClick={() => jumpToTable(c.table_name)}
                        className="w-full flex items-center justify-between text-left text-[11px] px-2 py-1 rounded hover:bg-white/5 transition-all"
                      >
                        <span className="font-mono text-white/70">
                          {c.column_name}
                        </span>
                        <span className="text-white/30">{c.table_name}</span>
                      </button>
                    ))}
                  {columnsMetadata.filter(
                    (c) =>
                      c.sensitivity ===
                      (statsFocus === "pii" ? "PII" : "Confidential"),
                  ).length === 0 && (
                    <p className="text-[11px] text-white/25 px-2 py-1">
                      No columns flagged.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Clarification cards — collapsed by default so the review
                table and Publish button stay reachable without scrolling
                past every open question first. */}
            {clarifications.length > 0 && (
              <div
                className="rounded-2xl border border-yellow-500/20 p-5"
                style={{ background: "rgba(250,204,21,0.03)" }}
              >
                <button
                  onClick={() => setClarificationsExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-yellow-400"
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle size={14} />
                    Clarification Needed ({clarifications.length} columns)
                  </span>
                  {clarificationsExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
                {!clarificationsExpanded && (
                  <p className="text-[11px] text-white/30 mt-1">
                    Optional — answer these to improve descriptions, or leave
                    as-is and publish anytime. Click to expand.
                  </p>
                )}
                {clarificationsExpanded && (
                  <div className="space-y-3 mt-3">
                    {clarifications.map((clar, i) => {
                      const groupCols = clar.columns || [clar.column_name];
                      const isGrouped = groupCols.length > 1;
                      return (
                        <div
                          key={i}
                          className="rounded-xl border border-white/8 p-4"
                          style={{ background: "rgba(28,28,42,0.5)" }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {isGrouped ? (
                                  <>
                                    <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/20 text-[11px] text-yellow-400 font-medium">
                                      {groupCols.length} columns
                                    </span>
                                    {groupCols.map((cn) => (
                                      <span
                                        key={cn}
                                        className="font-mono text-[11px] text-white/50 bg-white/5 px-1.5 py-0.5 rounded"
                                      >
                                        {cn}
                                      </span>
                                    ))}
                                  </>
                                ) : (
                                  <span className="font-mono text-xs text-white/70">
                                    {clar.column_name}
                                  </span>
                                )}
                                <SensitivityBadge level={clar.sensitivity} />
                                <ConfidenceBar value={clar.confidence} />
                              </div>
                              <p className="text-xs text-yellow-300/80 mb-2">
                                {clar.question}
                              </p>
                              {clar.current_description && (
                                <p className="text-[11px] text-white/25 mb-2">
                                  Current: {clar.current_description}
                                </p>
                              )}
                              <div className="flex gap-2">
                                <input
                                  value={clarifyAnswers[clar.column_name] || ""}
                                  onChange={(e) =>
                                    setClarifyAnswers((prev) => ({
                                      ...prev,
                                      [clar.column_name]: e.target.value,
                                    }))
                                  }
                                  placeholder={
                                    isGrouped
                                      ? `Answer for all ${groupCols.length} columns...`
                                      : "Your answer..."
                                  }
                                  className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/80 focus:border-brand-500/50 outline-none"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleClarify(
                                        clar.column_name,
                                        groupCols,
                                      );
                                  }}
                                />
                                <button
                                  onClick={() =>
                                    handleClarify(clar.column_name, groupCols)
                                  }
                                  disabled={
                                    clarifyLoading === clar.column_name ||
                                    !clarifyAnswers[clar.column_name]?.trim()
                                  }
                                  className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 text-white rounded-lg text-xs transition-colors"
                                >
                                  {clarifyLoading === clar.column_name ? (
                                    <Loader2
                                      size={12}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Send size={12} />
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleClarify(
                                      clar.column_name,
                                      groupCols,
                                      "No information available",
                                    )
                                  }
                                  disabled={clarifyLoading === clar.column_name}
                                  title="Mark as no information available and move on"
                                  className="px-3 py-1.5 border border-white/10 text-white/40 hover:text-white/60 hover:border-white/20 disabled:opacity-30 rounded-lg text-xs transition-colors whitespace-nowrap"
                                >
                                  No info available
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
                <Search size={12} className="text-white/30" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search columns..."
                  className="bg-transparent text-xs text-white/70 outline-none w-40"
                />
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
                <Table2 size={12} className="text-white/30" />
                <input
                  value={searchTable}
                  onChange={(e) => setSearchTable(e.target.value)}
                  placeholder="Search tables..."
                  className="bg-transparent text-xs text-white/70 outline-none w-36"
                />
              </div>
              <select
                value={filterSensitivity}
                onChange={(e) => setFilterSensitivity(e.target.value)}
                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 outline-none"
              >
                <option value="all">All Sensitivity</option>
                <option value="PII">PII</option>
                <option value="Confidential">Confidential</option>
                <option value="Internal">Internal</option>
                <option value="Public">Public</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 outline-none"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="enriched">Enriched</option>
                <option value="clarified">Clarified</option>
                <option value="approved">Approved</option>
              </select>
              {isMultiSheet && (
                <select
                  value={filterSheet}
                  onChange={(e) => setFilterSheet(e.target.value)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/60 outline-none"
                >
                  <option value="all">All Sheets</option>
                  {sheetNames.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
              <div className="flex-1" />
              <button
                data-testid="metadata-approve-all-btn"
                onClick={() => handleApproveAll()}
                className="px-4 py-1.5 bg-green-600/20 border border-green-500/30 text-green-400 rounded-lg text-xs font-medium hover:bg-green-600/30 transition-all flex items-center gap-1"
              >
                <CheckCircle2 size={12} /> Approve All
              </button>
            </div>

            {/* Actions — kept at the top so they're reachable without
                scrolling past every table's detail */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setStep(2)}
                className="px-5 py-2.5 border border-white/10 text-white/50 rounded-xl text-sm hover:border-white/20 transition-all"
              >
                <ArrowLeft size={14} className="inline mr-1" /> Back
              </button>
              <button
                data-testid="metadata-publish-btn"
                onClick={handlePublish}
                disabled={stats.approved === 0}
                className="px-8 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <CheckCircle2 size={14} /> Publish Catalog
              </button>
            </div>

            {/* Metadata review table – grouped per table, compact rows with expandable detail */}
            {Object.entries(
              isMultiSheet
                ? groupedBySheet(filteredColumns)
                : { [sheetNames[0] || "default"]: filteredColumns },
            ).map(([tableName, groupCols]) => (
              <div
                key={tableName}
                ref={(el) => {
                  sheetRefs.current[tableName] = el;
                }}
                className="space-y-2"
              >
                {isMultiSheet && (
                  <div className="flex items-center justify-between px-1">
                    <h4 className="text-xs font-semibold text-white/50 flex items-center gap-2">
                      <Table2 size={12} className="text-brand-400" />
                      {tableName}
                      <span className="text-[11px] text-white/25 font-normal">
                        ({groupCols.length} columns)
                      </span>
                    </h4>
                    <button
                      data-testid="metadata-approve-table-btn"
                      data-table={tableName}
                      onClick={() => handleApproveAll(tableName)}
                      className="px-3 py-1 bg-green-600/15 border border-green-500/25 text-green-400 rounded-lg text-[11px] font-medium hover:bg-green-600/25 transition-all flex items-center gap-1"
                    >
                      <CheckCircle2 size={11} /> Approve Table
                    </button>
                  </div>
                )}
                <div
                  className="rounded-2xl border border-white/8 overflow-hidden"
                  style={{ background: "rgba(28,28,42,0.5)" }}
                >
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto custom-scrollbar">
                    <table
                      data-testid="metadata-review-table"
                      className="w-full text-xs"
                    >
                      <thead className="bg-white/3 sticky top-0 z-10">
                        <tr className="text-white/40 text-left">
                          <th className="px-3 py-2 w-6"></th>
                          <th className="px-3 py-2">Column</th>
                          <th className="px-3 py-2">Sensitivity</th>
                          <th className="px-3 py-2">Confidence</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupCols.map((col, i) => {
                          const isExpanded = expandedRow === col.column_name;
                          const isEditing = editingCol === col.column_name;
                          return (
                            <React.Fragment key={i}>
                              {/* Compact summary row */}
                              <tr
                                data-testid="metadata-review-row"
                                data-column={col.column_name}
                                className={`border-t border-white/3 cursor-pointer transition-colors ${isExpanded ? "bg-white/4" : "hover:bg-white/2"}`}
                                onClick={() =>
                                  setExpandedRow(
                                    isExpanded ? null : col.column_name,
                                  )
                                }
                              >
                                <td className="px-3 py-2.5 text-white/30">
                                  {isExpanded ? (
                                    <ChevronDown size={12} />
                                  ) : (
                                    <ChevronRight size={12} />
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <span className="font-mono text-white/80 text-[13px]">
                                      {col.column_name}
                                    </span>
                                    <KeyBadges col={col} />
                                  </div>
                                  <div className="text-[10px] text-white/25 mt-0.5">
                                    {col.native_type || col.data_type}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5">
                                  <SensitivityBadge level={col.sensitivity} />
                                </td>
                                <td className="px-3 py-2.5">
                                  <ConfidenceBar value={col.confidence} />
                                </td>
                                <td className="px-3 py-2.5">
                                  <StatusBadge status={col.status} />
                                </td>
                                <td className="px-3 py-2.5">
                                  <div
                                    className="flex items-center gap-1 justify-end"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {isEditing ? (
                                      <>
                                        <button
                                          data-testid="metadata-save-edit-btn"
                                          data-column={col.column_name}
                                          onClick={() =>
                                            handleSaveEdit(col.column_name)
                                          }
                                          className="p-1 rounded bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 transition-all"
                                        >
                                          <Check size={12} />
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingCol(null);
                                            setEditFields({});
                                          }}
                                          className="p-1 rounded bg-white/5 text-white/30 hover:bg-white/10 transition-all"
                                        >
                                          <X size={12} />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          data-testid="metadata-edit-btn"
                                          data-column={col.column_name}
                                          onClick={() => {
                                            setEditingCol(col.column_name);
                                            setEditFields({});
                                            setExpandedRow(col.column_name);
                                          }}
                                          className="p-1 rounded hover:bg-white/5 text-white/25 hover:text-white/60 transition-all"
                                          title="Edit"
                                        >
                                          <Edit3 size={12} />
                                        </button>
                                        {col.status !== "approved" &&
                                          col.status !== "published" && (
                                            <button
                                              onClick={() =>
                                                handleApprove(col.column_name)
                                              }
                                              className="p-1 rounded hover:bg-green-500/10 text-white/25 hover:text-green-400 transition-all"
                                              title="Approve"
                                            >
                                              <CheckCircle2 size={12} />
                                            </button>
                                          )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded detail panel */}
                              {isExpanded && (
                                <tr className="border-t border-white/3">
                                  <td colSpan={6} className="px-0 py-0">
                                    <div className="px-6 py-4 bg-white/[0.02] border-b border-white/5 animate-fade-in">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                                        {/* Description */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            Description
                                          </label>
                                          {isEditing ? (
                                            <input
                                              value={
                                                editFields.description ??
                                                col.description
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  description: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                            />
                                          ) : (
                                            <p className="text-xs text-white/60 leading-relaxed">
                                              {col.description || "—"}
                                            </p>
                                          )}
                                        </div>

                                        {/* Business Term */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            Business Term
                                          </label>
                                          {isEditing ? (
                                            <input
                                              data-testid="metadata-edit-business-term"
                                              value={
                                                editFields.business_term ??
                                                (col.business_term || "")
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  business_term: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                            />
                                          ) : (
                                            <p className="text-xs text-white/60">
                                              {col.business_term || "—"}
                                            </p>
                                          )}
                                        </div>

                                        {/* Domain */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            Domain
                                          </label>
                                          {isEditing ? (
                                            <input
                                              value={
                                                editFields.domain ?? col.domain
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  domain: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                            />
                                          ) : (
                                            <p className="text-xs text-white/60">
                                              {col.domain || "—"}
                                            </p>
                                          )}
                                        </div>

                                        {/* Sensitivity (editable) */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            Sensitivity
                                          </label>
                                          {isEditing ? (
                                            <select
                                              value={
                                                editFields.sensitivity ??
                                                col.sensitivity
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  sensitivity: e.target.value,
                                                }))
                                              }
                                              className="px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                            >
                                              <option value="Public">
                                                Public
                                              </option>
                                              <option value="Internal">
                                                Internal
                                              </option>
                                              <option value="Confidential">
                                                Confidential
                                              </option>
                                              <option value="PII">PII</option>
                                            </select>
                                          ) : (
                                            <SensitivityBadge
                                              level={col.sensitivity}
                                            />
                                          )}
                                        </div>

                                        {/* Evidence */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            Evidence
                                          </label>
                                          <p className="text-[11px] text-white/40 italic">
                                            {col.evidence || "—"}
                                          </p>
                                        </div>

                                        {/* PII Category */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                            PII Category
                                          </label>
                                          <p className="text-xs text-white/60">
                                            {col.pii_category || "—"}
                                          </p>
                                        </div>

                                        {/* Data Owner */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <User
                                              size={10}
                                              className="text-white/20"
                                            />{" "}
                                            Data Owner
                                          </label>
                                          {isEditing ? (
                                            <input
                                              value={
                                                editFields.data_owner ??
                                                (col.data_owner || "")
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  data_owner: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                              placeholder="e.g. Finance Team"
                                            />
                                          ) : (
                                            <p className="text-xs text-white/60">
                                              {col.data_owner || "—"}
                                            </p>
                                          )}
                                        </div>

                                        {/* Data Steward */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <Shield
                                              size={10}
                                              className="text-white/20"
                                            />{" "}
                                            Data Steward
                                          </label>
                                          {isEditing ? (
                                            <input
                                              value={
                                                editFields.data_steward ??
                                                (col.data_steward || "")
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  data_steward: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                              placeholder="e.g. John Doe"
                                            />
                                          ) : (
                                            <p className="text-xs text-white/60">
                                              {col.data_steward || "—"}
                                            </p>
                                          )}
                                        </div>

                                        {/* Tags */}
                                        <div>
                                          <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <Tag
                                              size={10}
                                              className="text-white/20"
                                            />{" "}
                                            Tags
                                          </label>
                                          {isEditing ? (
                                            <input
                                              value={
                                                editFields.tags ??
                                                (col.tags?.join(", ") || "")
                                              }
                                              onChange={(e) =>
                                                setEditFields((prev) => ({
                                                  ...prev,
                                                  tags: e.target.value,
                                                }))
                                              }
                                              className="w-full px-2 py-1.5 bg-white/5 border border-brand-500/40 rounded text-xs text-white/80 outline-none"
                                              placeholder="comma-separated tags"
                                            />
                                          ) : (
                                            <div className="flex gap-1 flex-wrap">
                                              {col.tags &&
                                              col.tags.length > 0 ? (
                                                col.tags.map(
                                                  (t: string, ti: number) => (
                                                    <span
                                                      key={ti}
                                                      className="px-2 py-0.5 rounded bg-brand-500/10 text-[11px] text-brand-400/70 border border-brand-500/15"
                                                    >
                                                      {t}
                                                    </span>
                                                  ),
                                                )
                                              ) : (
                                                <span className="text-xs text-white/40">
                                                  —
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Stats row */}
                                      <div className="mt-4 flex gap-4 flex-wrap">
                                        <div
                                          className="rounded-lg border border-white/5 px-3 py-1.5"
                                          style={{
                                            background: "rgba(28,28,42,0.6)",
                                          }}
                                        >
                                          <span className="text-[11px] text-white/25 mr-2">
                                            Null %
                                          </span>
                                          <span className="text-xs text-white/60 font-mono">
                                            {col.null_pct != null
                                              ? `${(col.null_pct * 100).toFixed(1)}%`
                                              : "—"}
                                          </span>
                                        </div>
                                        <div
                                          className="rounded-lg border border-white/5 px-3 py-1.5"
                                          style={{
                                            background: "rgba(28,28,42,0.6)",
                                          }}
                                        >
                                          <span className="text-[11px] text-white/25 mr-2">
                                            Unique
                                          </span>
                                          <span className="text-xs text-white/60 font-mono">
                                            {col.unique_count ?? "—"}
                                          </span>
                                        </div>
                                        <div
                                          className="rounded-lg border border-white/5 px-3 py-1.5"
                                          style={{
                                            background: "rgba(28,28,42,0.6)",
                                          }}
                                        >
                                          <span className="text-[11px] text-white/25 mr-2">
                                            Cardinality
                                          </span>
                                          <span className="text-xs text-white/60 font-mono">
                                            {col.cardinality_ratio != null
                                              ? `${(col.cardinality_ratio * 100).toFixed(1)}%`
                                              : "—"}
                                          </span>
                                        </div>
                                        <div
                                          className="rounded-lg border border-white/5 px-3 py-1.5"
                                          style={{
                                            background: "rgba(28,28,42,0.6)",
                                          }}
                                        >
                                          <span className="text-[11px] text-white/25 mr-2">
                                            Semantic
                                          </span>
                                          <span className="text-xs text-white/60">
                                            {col.semantic_type || "—"}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Distinct code values (Gender M/F, Status Active/Inactive, etc.) */}
                                      {col.distinct_values &&
                                        col.distinct_values.length > 0 && (
                                          <div className="mt-3">
                                            <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                              Distinct Code Values
                                            </label>
                                            <div className="flex gap-1.5 flex-wrap">
                                              {col.distinct_values.map(
                                                (v, vi) => (
                                                  <span
                                                    key={vi}
                                                    className="px-2 py-0.5 rounded bg-brand-500/10 text-[11px] text-brand-400 font-mono border border-brand-500/20"
                                                  >
                                                    {String(v)}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}

                                      {/* Sample values */}
                                      {col.sample_values &&
                                        col.sample_values.length > 0 && (
                                          <div className="mt-3">
                                            <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                                              Sample Values
                                            </label>
                                            <div className="flex gap-1.5 flex-wrap">
                                              {col.sample_values
                                                .slice(0, 8)
                                                .map((v, vi) => (
                                                  <span
                                                    key={vi}
                                                    className="px-2 py-0.5 rounded bg-white/5 text-[11px] text-white/40 font-mono border border-white/5"
                                                  >
                                                    {String(v).length > 30
                                                      ? String(v).slice(0, 30) +
                                                        "…"
                                                      : String(v)}
                                                  </span>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* STEP 5 — Publish & Export */}
        {/* ═══════════════════════════════════════════════════ */}
        {step === 5 && !loading && (
          <div
            className="space-y-6 animate-fade-in"
            data-testid="metadata-publish-success"
          >
            {/* Success banner */}
            <div
              className="rounded-2xl border border-green-500/30 p-6 text-center"
              style={{ background: "rgba(34,197,94,0.05)" }}
            >
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-green-300 mb-1">
                Metadata Published!
              </h2>
              <p className="text-sm text-white/40">
                {stats.published} columns cataloged and published from{" "}
                {sourceName}
              </p>
            </div>

            {/* Dataset Summary */}
            {datasetSummary && (
              <div
                className="rounded-2xl border border-brand-500/20 p-6"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
                  <Sparkles size={14} className="text-brand-400" />
                  AI Dataset Summary
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">
                  {datasetSummary}
                </p>
              </div>
            )}

            {/* Per-Table Descriptions */}
            {Object.keys(tableDescriptions).length > 0 && (
              <div
                className="rounded-2xl border border-white/8 p-6"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <h3 className="text-sm font-semibold text-white/70 mb-4 flex items-center gap-2">
                  <Table2 size={14} className="text-brand-400" />
                  Table / Sheet Descriptions
                </h3>
                <div className="space-y-3">
                  {Object.entries(tableDescriptions).map(
                    ([tableName, desc]) => (
                      <div
                        key={tableName}
                        className="rounded-xl border border-white/5 p-4"
                        style={{ background: "rgba(14,14,24,0.4)" }}
                      >
                        <h4 className="text-xs font-mono font-semibold text-brand-400 mb-1.5">
                          {tableName}
                        </h4>
                        <p className="text-xs text-white/50 leading-relaxed">
                          {desc}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Export options */}
            <div
              className="rounded-2xl border border-white/8 p-4"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <Download size={14} className="text-brand-400" />
                  Export Catalog
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1 p-0.5 bg-white/3 border border-white/8 rounded-lg">
                    {["json", "csv", "yaml", "excel", "pdf"].map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => setExportFormat(fmt)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                          exportFormat === fmt
                            ? "bg-brand-500/20 text-brand-400"
                            : "text-white/40 hover:text-white/60"
                        }`}
                      >
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <a
                    href={
                      sessionId
                        ? api.metadataExportUrl(sessionId, exportFormat, {
                            minimal: true,
                          })
                        : "#"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <Download size={12} /> Download
                  </a>
                </div>
              </div>
            </div>

            {/* DDL Generator — pulls DDL straight from the profiled source
                (native source types), translated for the chosen target DB */}
            <div
              className="rounded-2xl border border-white/8 p-6"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <h3 className="text-sm font-semibold text-white/70 mb-4 flex items-center gap-2">
                <Code2 size={14} className="text-brand-400" />
                DDL Generator
              </h3>
              <p className="text-xs text-white/35 mb-4 -mt-2">
                Extracts DDL from the discovered source schema and translates it
                for the target database below (types, identity columns, keys) so
                it runs without syntax errors on the target system.
              </p>

              {/* Database selector */}
              <div className="mb-4">
                <label className="text-[11px] text-white/30 uppercase tracking-wider mb-2 block">
                  Target Database
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(supportedDbs.length > 0
                    ? supportedDbs
                    : [
                        { id: "snowflake", label: "Snowflake" },
                        { id: "oracle", label: "Oracle" },
                        { id: "databricks", label: "Databricks" },
                        { id: "postgresql", label: "PostgreSQL" },
                        { id: "mysql", label: "MySQL" },
                        { id: "sqlserver", label: "SQL Server" },
                        { id: "bigquery", label: "BigQuery" },
                        { id: "redshift", label: "Redshift" },
                      ]
                  ).map((db) => (
                    <button
                      key={db.id}
                      onClick={() => setDdlDb(db.id)}
                      className={`px-3 py-2 rounded-lg border text-xs text-center transition-all ${
                        ddlDb === db.id
                          ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                          : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                      }`}
                    >
                      {db.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* DDL Type */}
              <div className="mb-4">
                <label className="text-[11px] text-white/30 uppercase tracking-wider mb-2 block">
                  DDL Type
                </label>
                <div className="flex gap-2">
                  {[
                    { id: "create_table", label: "CREATE TABLE" },
                    { id: "create_view", label: "CREATE VIEW" },
                    { id: "alter_comments", label: "ALTER / ADD COMMENTS" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setDdlType(t.id)}
                      className={`px-4 py-2 rounded-lg border text-xs transition-all ${
                        ddlType === t.id
                          ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                          : "border-white/8 bg-white/3 text-white/40 hover:border-white/15"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Table selection — extract DDL for all tables or a chosen set */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] text-white/30 uppercase tracking-wider">
                    Tables
                  </label>
                  <div className="flex items-center gap-1 p-0.5 bg-white/3 border border-white/8 rounded-lg">
                    <button
                      onClick={() => setDdlTableMode("all")}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                        ddlTableMode === "all"
                          ? "bg-brand-500/20 text-brand-400"
                          : "text-white/40 hover:text-white/60"
                      }`}
                    >
                      All Tables ({sheetNames.length})
                    </button>
                    <button
                      onClick={() => setDdlTableMode("selected")}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                        ddlTableMode === "selected"
                          ? "bg-brand-500/20 text-brand-400"
                          : "text-white/40 hover:text-white/60"
                      }`}
                    >
                      Select Tables
                    </button>
                  </div>
                </div>
                {ddlTableMode === "selected" && (
                  <div className="flex flex-wrap gap-1.5 p-2 rounded-lg bg-white/3 border border-white/8 max-h-32 overflow-y-auto custom-scrollbar">
                    {sheetNames.map((t) => {
                      const selected = ddlSelectedTables.includes(t);
                      return (
                        <button
                          key={t}
                          onClick={() => toggleDdlTable(t)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-mono transition-all ${
                            selected
                              ? "border-brand-500/50 bg-brand-500/10 text-brand-400"
                              : "border-white/8 text-white/40 hover:border-white/15"
                          }`}
                        >
                          {selected && <Check size={10} />}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Schema name */}
              <div className="mb-4">
                <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1 block">
                  Schema Name (optional)
                </label>
                <input
                  value={ddlSchemaName}
                  onChange={(e) => setDdlSchemaName(e.target.value)}
                  placeholder="e.g. analytics"
                  className="w-full md:w-64 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white/70 outline-none focus:border-brand-500/40"
                />
              </div>

              <button
                onClick={handleGenerateDDL}
                disabled={
                  ddlLoading ||
                  (ddlTableMode === "selected" &&
                    ddlSelectedTables.length === 0)
                }
                className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                {ddlLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Code2 size={14} />
                )}
                Generate DDL
              </button>

              {/* DDL Output */}
              {ddlResult && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-white/30 uppercase tracking-wider">
                      {ddlResult.db_type} —{" "}
                      {ddlResult.ddl_type.replace("_", " ")} —{" "}
                      {ddlResult.column_count} columns
                      {ddlResult.table_names && ddlResult.table_names.length > 1
                        ? ` across ${ddlResult.table_names.length} tables`
                        : ""}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleDownloadDDL}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-white/50 transition-all"
                      >
                        <Download size={12} />
                        .sql
                      </button>
                      <button
                        onClick={handleCopyDDL}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-white/50 transition-all"
                      >
                        {ddlCopied ? (
                          <ClipboardCheck
                            size={12}
                            className="text-green-400"
                          />
                        ) : (
                          <Copy size={12} />
                        )}
                        {ddlCopied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                  <pre className="p-4 rounded-xl bg-black/30 border border-white/5 text-xs text-white/60 font-mono overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar whitespace-pre-wrap">
                    {ddlResult.ddl}
                  </pre>
                </div>
              )}
            </div>

            {/* Business Glossary — Rich Table */}
            {(glossaryEntries.length > 0 || glossaryMarkdown) && (
              <div
                data-testid="metadata-glossary"
                className="rounded-2xl border border-white/8 overflow-hidden"
                style={{ background: "rgba(28,28,42,0.5)" }}
              >
                <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
                    <FileText size={14} className="text-brand-400" />
                    Business Glossary ({glossaryEntries.length || "—"} terms)
                  </h3>
                  <div className="flex gap-2 items-center">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg">
                      <Search size={11} className="text-white/25" />
                      <input
                        value={glossarySearch}
                        onChange={(e) => setGlossarySearch(e.target.value)}
                        placeholder="Search terms..."
                        className="bg-transparent text-[11px] text-white/60 outline-none w-28"
                      />
                    </div>
                    <select
                      value={glossaryDomainFilter}
                      onChange={(e) => setGlossaryDomainFilter(e.target.value)}
                      className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white/50 outline-none"
                    >
                      <option value="all">All Domains</option>
                      {[
                        ...new Set(
                          glossaryEntries.map((g) => g.domain).filter(Boolean),
                        ),
                      ].map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                  {glossaryEntries.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead className="bg-white/3 sticky top-0 z-10">
                        <tr className="text-white/40 text-left">
                          <th className="px-4 py-2.5">Business Term</th>
                          <th className="px-4 py-2.5">Column</th>
                          <th className="px-4 py-2.5">Description</th>
                          <th className="px-4 py-2.5">Domain</th>
                          <th className="px-4 py-2.5">Type</th>
                          <th className="px-4 py-2.5">Sensitivity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {glossaryEntries
                          .filter((g) => {
                            if (
                              glossaryDomainFilter !== "all" &&
                              g.domain !== glossaryDomainFilter
                            )
                              return false;
                            if (glossarySearch) {
                              const s = glossarySearch.toLowerCase();
                              return (
                                g.business_term.toLowerCase().includes(s) ||
                                g.column_name.toLowerCase().includes(s) ||
                                g.description.toLowerCase().includes(s)
                              );
                            }
                            return true;
                          })
                          .map((g, i) => (
                            <tr
                              key={i}
                              data-testid="metadata-glossary-row"
                              data-column={g.column_name}
                              className="border-t border-white/3 hover:bg-white/2"
                            >
                              <td className="px-4 py-2.5">
                                <span className="text-white/80 font-medium">
                                  {g.business_term}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <code className="text-[11px] text-brand-400/70 bg-brand-500/5 px-1.5 py-0.5 rounded">
                                  {g.column_name}
                                </code>
                              </td>
                              <td className="px-4 py-2.5 max-w-[280px]">
                                <span className="text-white/50 leading-relaxed">
                                  {g.description || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="px-2 py-0.5 rounded bg-white/5 text-white/40 text-[11px]">
                                  {g.domain || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="text-white/35 text-[11px]">
                                  {g.data_type}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <SensitivityBadge level={g.sensitivity} />
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  ) : (
                    <pre className="p-4 text-xs text-white/40 whitespace-pre-wrap">
                      {glossaryMarkdown}
                    </pre>
                  )}
                </div>
              </div>
            )}

            {/* Summary table */}
            <div
              className="rounded-2xl border border-white/8 overflow-hidden"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <div className="px-5 py-3 border-b border-white/5">
                <h3 className="text-sm font-semibold text-white/70">
                  Published Metadata ({columnsMetadata.length} columns)
                </h3>
              </div>
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                <table className="w-full text-xs">
                  <thead className="bg-white/3 sticky top-0 z-10">
                    <tr className="text-white/40 text-left">
                      <th className="px-4 py-2">Table / Sheet</th>
                      <th className="px-4 py-2">Column</th>
                      <th className="px-4 py-2">Description</th>
                      <th className="px-4 py-2">Business Term</th>
                      <th className="px-4 py-2">Domain</th>
                      <th className="px-4 py-2">Sensitivity</th>
                      <th className="px-4 py-2">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let lastTable = "";
                      return columnsMetadata.map((col, i) => {
                        const showTable = col.table_name !== lastTable;
                        lastTable = col.table_name;
                        return (
                          <tr
                            key={i}
                            className={`border-t ${showTable ? "border-white/10" : "border-white/3"}`}
                          >
                            <td className="px-4 py-2 text-white/30 text-[11px]">
                              {showTable ? (
                                <span className="px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-400 font-medium">
                                  {col.table_name}
                                </span>
                              ) : null}
                            </td>
                            <td className="px-4 py-2 font-mono text-white/70">
                              {col.column_name}
                              <KeyBadges col={col} />
                            </td>
                            <td className="px-4 py-2 text-white/50 max-w-[200px] truncate">
                              {col.description || "—"}
                            </td>
                            <td className="px-4 py-2 text-white/50">
                              {col.business_term || "—"}
                            </td>
                            <td className="px-4 py-2 text-white/40">
                              {col.domain}
                            </td>
                            <td className="px-4 py-2">
                              <SensitivityBadge level={col.sensitivity} />
                            </td>
                            <td className="px-4 py-2">
                              <ConfidenceBar value={col.confidence} />
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Saved confirmation */}
            <div
              className="rounded-xl border border-green-500/15 px-4 py-2.5 flex items-center gap-2"
              style={{ background: "rgba(34,197,94,0.03)" }}
            >
              <CheckCircle2 size={13} className="text-green-500/50" />
              <span className="text-[11px] text-green-400/60">
                Session saved automatically. You can resume and add more
                definitions anytime from the Start screen.
              </span>
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setStep(4)}
                className="px-5 py-2.5 border border-white/10 text-white/50 rounded-xl text-sm hover:border-white/20 transition-all"
              >
                <ArrowLeft size={14} className="inline mr-1" /> Back to Review
              </button>
              <button
                onClick={onBack}
                className="px-8 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Back to Agent Catalog
              </button>
            </div>
          </div>
        )}
      </div>

      <ConnectDatabaseDialog
        open={showSharedConnectDialog}
        onClose={() => setShowSharedConnectDialog(false)}
        title="Connect a Saved Database"
        projectId={projectId}
        agentKey="metadata_curator"
        extraField={{
          key: "table",
          label: "Table (optional)",
          value: dbConfig.table || "",
          onChange: (v) =>
            setDbConfig((p) => ({ ...p, table: v || undefined })),
          placeholder: "Leave blank to list all",
        }}
        testIdPrefix="metadata-saved"
        onConnectExisting={handleSharedDialogConnectExisting}
        onConnectNew={handleSharedDialogConnectNew}
      />

      {/* Floating chat — docked to the side so it never needs scrolling to
          reach. Restricted to Step 5: earlier steps haven't run enrichment
          yet, so most questions (PII, description, domain) can't be answered
          and showing it sooner would just confuse users. */}
      {sessionId && step === 5 && (
        <>
          {chatPanelOpen && (
            <div
              className="fixed bottom-24 right-6 z-40 w-80 max-h-[28rem] rounded-2xl border border-white/8 overflow-hidden shadow-2xl flex flex-col"
              style={{ background: "rgba(20,20,32,0.97)" }}
            >
              <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles size={12} className="text-brand-400" />
                  <span className="text-xs text-white/50">
                    Ask about the metadata
                  </span>
                </div>
                <button
                  onClick={() => setChatPanelOpen(false)}
                  className="text-white/20 hover:text-white/50"
                >
                  <X size={14} />
                </button>
              </div>
              <label
                className="flex items-center gap-1.5 text-[11px] text-white/40 cursor-pointer select-none px-4 py-1.5 border-b border-white/5"
                title="When on, chat only answers from already-computed metadata and never calls an LLM — nothing leaves this session."
              >
                <input
                  type="checkbox"
                  checked={chatDeterministicOnly}
                  onChange={(e) => setChatDeterministicOnly(e.target.checked)}
                  className="accent-brand-500"
                />
                No LLM (deterministic only)
              </label>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
                {chatHistory.length === 0 && (
                  <p className="text-[11px] text-white/25 text-center py-6">
                    Ask a question about any table or column in the published
                    catalog.
                  </p>
                )}
                {chatHistory.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-xl text-xs ${
                        msg.role === "user"
                          ? "bg-brand-500/20 text-brand-300"
                          : "bg-white/5 text-white/60"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-xl bg-white/5">
                      <Loader2
                        size={14}
                        className="text-white/30 animate-spin"
                      />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div className="px-4 py-2 border-t border-white/5 flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleChat();
                    }
                  }}
                  placeholder="Ask about the metadata..."
                  className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/80 focus:border-brand-500/50 outline-none"
                />
                <button
                  onClick={handleChat}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 text-white rounded-lg transition-colors"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          )}
          <button
            onClick={() => setChatPanelOpen((prev) => !prev)}
            title="Ask about the metadata"
            className="fixed bottom-6 right-24 z-40 w-12 h-12 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-lg flex items-center justify-center transition-all"
          >
            {chatPanelOpen ? <X size={18} /> : <MessageCircle size={18} />}
          </button>
        </>
      )}
    </div>
  );
}
